import { AD_TITLE_MAX, plural } from "@/lib/plural";
import { isAppMode } from "@/app-bridge/core/wrapper";
import { IMAGE_ACCEPT, normalizeImageFile } from "@/lib/image-input";
const StartCaptureDialog = lazy(() =>
  import("@/components/StartCaptureDialog").then((m) => ({ default: m.StartCaptureDialog })),
);
const ScanModePicker = lazy(() =>
  import("@/components/scan/ScanModePicker").then((m) => ({ default: m.ScanModePicker })),
);
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "@/components/ui/sheet";
import { useIsMobile } from "@/hooks/use-mobile";
import type { AdminTraceStep } from "@/lib/admin-trace";
import { getMyRole } from "@/lib/admin.functions";
const AdminAiLog = lazy(() =>
  import("@/components/AdminAiLog").then((m) => ({ default: m.AdminAiLog })),
);

import { StepLabel, FieldWrap } from "@/components/ad-form/AdFormPanel";
import { Link, useNavigate, useBlocker } from "@tanstack/react-router";
import { useState, useRef, useCallback, useEffect, useMemo, Suspense, lazy } from "react";
import { LatestAdsGrid, LatestAdsSkeleton } from "@/components/home/LatestAdsGrid";
import { queryOptions, useQuery, useSuspenseQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import {
  Camera,
  Sparkles,
  Share2,
  Loader2,
  LocateFixed,

  RefreshCw,
  Check,
  ArrowLeft,
  ArrowRight,
  Lock,
  ShieldCheck,
  Plus,
  X,
  MapPin,
  RotateCw,
  Video,
  Images,
  Crown,
  AlertTriangle,
  TriangleAlert,
  Wand2,
  Undo2,
  Info,
  Gem,
  EyeOff,
} from "lucide-react";

import {
  generateAd,
  publishAd,
  listAds,
  getMyContactDefaults,
  createVideoUpload,
  checkAdPrice,
  getMyAds,
} from "@/lib/ads.functions";
const DisplayNameSetup = lazy(() =>
  import("@/components/ad-form/DisplayNameSetup").then((m) => ({ default: m.DisplayNameSetup })),
);
const HighValueDialog = lazy(() =>
  import("@/components/ad-form/HighValueDialog").then((m) => ({ default: m.HighValueDialog })),
);
import {
  clearDeepValuationDraft,
  readDeepValuationDraft,
  type DeepValuationDraft,
} from "@/components/scan/DeepValuationDialog";

import { track } from "@/lib/analytics-client";
import { LockedResultPanel } from "@/components/sell/LockedResultCard";
import { scrollToElement } from "@/lib/scroll";

import {
  formatPriceRange,
  priceFlagShort,
  priceFlagTitle,
  type PriceAssessment,
} from "@/lib/price-check";
import { AUCTION_DAYS, DEFAULT_AUCTION_DAYS, formatKr } from "@/lib/auctions";
import { useAuctionsEnabled } from "@/lib/auction-availability";
import { AuctionDevBadge } from "@/components/AuctionDevBadge";
import { type AdTextCheck } from "@/lib/moderation";
import { checkAdText, checkAdQuality } from "@/lib/moderation.functions";
import { QUALITY_OK, type QualityCheck, type PhotoQuality } from "@/lib/quality";
import { PhotoQualityNotice } from "@/components/sell/PhotoQualityNotice";
import { listSavedPlaces } from "@/lib/addresses.functions";
import { suggestLocalityFromPoint } from "@/lib/geo.functions";
import { usePermissionPrimer } from "@/app-bridge/components/PermissionPrimer";

import { AiInsightCard, type AiInsight } from "@/components/sell/AiInsightCard";
import { getMyPlanStatus } from "@/lib/plans.functions";
import { startMuted } from "@/lib/video-mute";
import { adVideoSrc } from "@/lib/video-src";
import { moveItem, useDragReorder } from "@/lib/use-drag-reorder";
import { DragGhost } from "@/components/DragGhost";
import type { AiProgressState } from "@/components/AiProgressModal";
const AiProgressModal = lazy(() =>
  import("@/components/AiProgressModal").then((m) => ({ default: m.AiProgressModal })),
);
import type { PublishFlowState, PublishIssue } from "@/components/PublishFlowModal";
const PublishFlowModal = lazy(() =>
  import("@/components/PublishFlowModal").then((m) => ({ default: m.PublishFlowModal })),
);
import { getPromotionStatus, promoteAd } from "@/lib/promotions.functions";

import { PLANS, remainingThisMonth, type PlanId, planAtLeast } from "@/lib/plans";
import { CATEGORIES } from "@/lib/categories";
import { VALUATION_DISCLAIMER } from "@/lib/deep-valuation";
import { AdCard, type AdCardData } from "@/components/AdCard";
import { useTopBids } from "@/hooks/useTopBids";
import { VideoScanPromo, PhotoScanPromo, DeepScanPromo } from "@/components/VideoScanPromo";
import { useExpertFeatures } from "@/hooks/useExpertFeatures";
import { useLocale } from "@/lib/i18n/locale-context";
import { HOW_IT_WORKS_STEPS } from "@/lib/how-it-works";
const AuthModal = lazy(() =>
  import("@/components/auth/AuthModal").then((m) => ({ default: m.AuthModal })),
);
import type { LegalDoc } from "@/components/legal/LegalModal";
const LegalModal = lazy(() =>
  import("@/components/legal/LegalModal").then((m) => ({ default: m.LegalModal })),
);
import { useAuth } from "@/hooks/useAuth";
import { useIsAppMode } from "@/app-bridge/hooks/useIsAppMode";
import { analyzeStudioImage, cleanImage, getCleanImageQuota } from "@/lib/clean-image.functions";
import type { CleanMode } from "@/components/sell/ImageStudioDialog";
const ImageStudioDialog = lazy(() =>
  import("@/components/sell/ImageStudioDialog").then((m) => ({ default: m.ImageStudioDialog })),
);
import type { CleanImageQuota, StudioSubject } from "@/lib/clean-image";

import { DEFAULT_CREDIT_SEK, formatSek } from "@/lib/ai-costs";
import { useOnline } from "@/app-bridge/hooks/useOnline";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Skeleton } from "@/components/ui/skeleton";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { ModerationNotice } from "@/components/ModerationNotice";
import { toast } from "sonner";
import { Checkbox } from "@/components/ui/checkbox";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { LocalityCombobox, LocalityHelp } from "@/components/LocalityCombobox";
import { AdCountryPicker } from "@/components/sell/AdCountryPicker";
import { localizedPath } from "@/lib/i18n/slug-map";
const SavedPlacesPicker = lazy(() =>
  import("@/components/SavedPlacesPicker").then((m) => ({ default: m.SavedPlacesPicker })),
);
import {
  AUCTION_MIN_START_PRICE,
  adFieldError,
  auctionReserveFloor,
  auctionValueWarning,
  isValidLocalityForCountry,
  recommendedReservePrice,
  reservePriceWarning,
} from "@/lib/ad-rules";

import { cn } from "@/lib/utils";
import { rememberReturn } from "@/lib/return-to";
import { setPendingAdFiles, takePendingAdFiles } from "@/lib/pending-ad-files";
import {
  loadDraft,
  saveDraft,
  clearDraft,
  getDraftId,
  type PendingGroup,
} from "@/lib/draft-store";
import { getServerDraft, saveServerDraft, deleteServerDraft } from "@/lib/drafts.functions";

import { SplitSuggestion } from "@/components/SplitSuggestion";

import { useSurfaceWords } from "@/lib/surface";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

export { latestAdsQueryOptions } from "@/lib/latest-ads";

const steps = HOW_IT_WORKS_STEPS;

const MAX_IMAGES = 6;

type AdImage = { imageBase64: string; mimeType: string };

type ContactFinding = {
  type: "qr_code" | "phone" | "email" | "website" | "social_media";
  imageIndex: number;
  evidence: string;
  confidence: "low" | "medium" | "high";
};

/** En grupp föremål som AI:n hittat bland bilderna. */
type SplitGroup = {
  label: string;
  imageIndexes: number[];
  title: string;
  description: string;
  price: number;
  category?: string;
  tags?: string[];
};

type DraftForm = {
  title: string;
  description: string;
  price: string;
  category: string;
  location: string;
  address: string;
  contactName: string;
  contactEmail: string;
  contactPhone: string;
  saleKind: "fixed" | "auction";
  reservePrice: string;
  auctionDays: "3" | "7" | "14";
  peopleInImages: "true" | "false";
  peopleConsent: "true" | "false";
  accepted: "true" | "false";
  saleAdviceKind: "" | "fixed" | "auction";
  saleAdviceReason: string;
  valuationLow: string;
  valuationHigh: string;
  priceMotivation: string;
  hidden: "true" | "false";
};

const emptyForm: DraftForm = {
  title: "",
  description: "",
  price: "",
  category: "ovrigt",
  location: "",
  address: "",
  contactName: "",
  contactEmail: "",
  contactPhone: "",
  saleKind: "fixed",
  reservePrice: "",
  auctionDays: "7",
  peopleInImages: "false",
  peopleConsent: "false",
  accepted: "false",
  saleAdviceKind: "",
  saleAdviceReason: "",
  valuationLow: "",
  valuationHigh: "",
  priceMotivation: "",
  hidden: "false",
};

const SELL_FLOW_SCROLL_KEY = "sellavo:annons-scroll";

/** Ny position för ett bildindex, eller null om bilden försvinner. */
type IndexMap = (i: number) => number | null;

const removeIndexMap =
  (removed: number): IndexMap =>
  (i) =>
    i === removed ? null : i > removed ? i - 1 : i;

const swapIndexMap =
  (a: number, b: number): IndexMap =>
  (i) =>
    i === a ? b : i === b ? a : i;

const moveIndexMap =
  (from: number, to: number): IndexMap =>
  (i) => {
    if (i === from) return to;
    if (from < to) return i > from && i <= to ? i - 1 : i;
    if (from > to) return i >= to && i < from ? i + 1 : i;
    return i;
  };

/** Flyttar indexnycklad studiodata (t.ex. cleanOriginals) när bilderna ändras. */
function remapNumKeyed<T>(rec: Record<number, T>, map: IndexMap): Record<number, T> {
  const next: Record<number, T> = {};
  for (const [k, v] of Object.entries(rec)) {
    const n = map(Number(k));
    if (n !== null && Number.isFinite(n)) next[n] = v;
  }
  return next;
}

/** Samma sak för nycklar som "index:mode" eller "index:mode:variant". */
function remapStrKeyed<T>(rec: Record<string, T>, map: IndexMap): Record<string, T> {
  const next: Record<string, T> = {};
  for (const [k, v] of Object.entries(rec)) {
    const [head, ...rest] = k.split(":");
    const n = map(Number(head));
    if (n !== null && Number.isFinite(n)) next[[n, ...rest].join(":")] = v;
  }
  return next;
}

export function SellFlow({ variant = "home" }: { variant?: "home" | "create" }) {
  const returPath = variant === "create" ? "/skapa" : "/";
  const navigate = useNavigate();

  const fileInputRef = useRef<HTMLInputElement>(null);
  const homeInputRef = useRef<HTMLInputElement>(null);
  const replaceImageIndexRef = useRef<number | null>(null);
  const photoNoticeRef = useRef<HTMLDivElement>(null);
  const { user, loading: authLoading } = useAuth();
  const [authOpen, setAuthOpen] = useState(false);
  const [authMode, setAuthMode] = useState<"login" | "register">("register");
  const [doc, setDoc] = useState<LegalDoc>(null);
  const [images, setImages] = useState<AdImage[]>([]);
  const [video, setVideo] = useState<{ path: string; name: string; poster: string | null } | null>(
    null,
  );
  const [loading, setLoading] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [form, setForm] = useState<DraftForm>(emptyForm);
  const [hasDraft, setHasDraft] = useState(false);
  const [heroPressed, setHeroPressed] = useState(false);
  const [modeSheetOpen, setModeSheetOpen] = useState(false);
  const [startDialogOpen, setStartDialogOpen] = useState(false);
  const isMobile = useIsMobile();
  const appMode = useIsAppMode();
  const [accepted, setAccepted] = useState(false);
  const [attempted, setAttempted] = useState(false);
  const [published, setPublished] = useState<{ slug: string; url: string } | null>(null);
  // Admin ser en logg över exakt vilka steg och modeller flödet använde.
  const [adminTrace, setAdminTrace] = useState<AdminTraceStep[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [moderation, setModeration] = useState<{
    check: AdTextCheck;
    title: string;
    description: string;
  } | null>(null);
  const [restored, setRestored] = useState(false);
  /** Bildstudions läge (original + versioner) för utkastsparningen. Ref: får inte trigga omsparning. */
  const studioDraftRef = useRef<{
    originals: Record<string, { imageBase64: string; mimeType: string }>;
    results: Record<string, { imageBase64: string; mimeType: string }>;
    variants: Record<string, number>;
    modes: Record<string, string>;
  }>({ originals: {}, results: {}, variants: {}, modes: {} });

  const [imageToRemove, setImageToRemove] = useState<number | null>(null);
  const [uploadProgress, setUploadProgress] = useState<{
    done: number;
    total: number;
    pct: number;
  } | null>(null);
  // Interna AI-taggar – visas aldrig, används bara för att matcha liknande annonser.
  const [aiTags, setAiTags] = useState<string[]>([]);
  const [regenerateOpen, setRegenerateOpen] = useState(false);
  const [aiProgress, setAiProgress] = useState<AiProgressState | null>(null);
  const [publishFlow, setPublishFlow] = useState<PublishFlowState | null>(null);
  // Sätts när priset är så högt att vi vill godkänna annonsen manuellt.
  const [priceApproval, setPriceApproval] = useState<{
    min: number | null;
    max: number | null;
  } | null>(null);
  const [priceMotivation, setPriceMotivation] = useState("");
  const surfaceText = useSurfaceWords();
  const [prefillNotice, setPrefillNotice] = useState<string | null>(null);
  // AI:ns bedömning av fotot + den annons som väntar när bilden inte duger.
  const [photoQuality, setPhotoQuality] = useState<PhotoQuality | null>(null);
  // AI:ns korta kommentar om bilden + vad den valde att sälja.
  const [aiInsight, setAiInsight] = useState<AiInsight | null>(null);
  // Sant när orten fylldes i automatiskt från en sparad adress.
  const [locationPrefilled, setLocationPrefilled] = useState(false);
  const [contactFinding, setContactFinding] = useState<ContactFinding | null>(null);
  const pendingAdRef = useRef<(() => void) | null>(null);
  useEffect(() => {
    if (!photoQuality || photoQuality.verdict !== "unusable") return;
    const frame = window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => {
        const el = photoNoticeRef.current;
        if (!el) return;
        const top = el.getBoundingClientRect().top + window.scrollY - 88;
        window.scrollTo({ top, behavior: "smooth" });
        window.setTimeout(() => {
          const current = photoNoticeRef.current?.getBoundingClientRect().top;
          if (typeof current === "number" && Math.abs(current - 88) > 40) {
            window.scrollBy({ top: current - 88, behavior: "smooth" });
          }
        }, 450);
      });
    });
    return () => window.cancelAnimationFrame(frame);
  }, [photoQuality]);
  // Rättar man något i formuläret ska den gamla "det här saknas"-listan försvinna
  // – annars ser det ut som att fältet fortfarande är tomt.
  useEffect(() => {
    setPublishFlow((f) => (f && f.phase === "blocked" ? null : f));
  }, [form]);

  // Fast pris eller auktion – auktionen får utgångspris, reservationspris och längd.
  const [saleKind, setSaleKind] = useState<"fixed" | "auction">("fixed");
  // Auktioner finns bara på dev/förhandsvisning tills sajten har trafik.
  const auctionsOn = useAuctionsEnabled();
  useEffect(() => {
    if (!auctionsOn) setSaleKind("fixed");
  }, [auctionsOn]);
  /** Vårt förslag på säljsätt från granskningen, med motivering. */
  const [saleAdvice, setSaleAdvice] = useState<{
    kind: "fixed" | "auction";
    reason: string;
  } | null>(null);
  /** Tar säljaren emot bud i meddelanden? På som standard. */
  const [acceptsOffers, setAcceptsOffers] = useState(true);
  const [hiddenAd, setHiddenAd] = useState(false);
  /** AI:ns korta motivering till bud-inställningen. */
  const [offersAdvice, setOffersAdvice] = useState("");
  // AI:ns värdering av prylen – används som skyddsnät mot för lågt utgångspris.
  const [aiValuation, setAiValuation] = useState<{ low: number; high: number | null } | null>(null);
  const [reservePrice, setReservePrice] = useState("");
  const [auctionDays, setAuctionDays] = useState<3 | 7 | 14>(DEFAULT_AUCTION_DAYS as 7);
  const [peopleInImages, setPeopleInImages] = useState(false);
  const [peopleConsent, setPeopleConsent] = useState(false);

  // Sparad prisbedömning så att "Publicera ändå" slipper köra AI:n igen.
  const priceAssessmentRef = useRef<PriceAssessment | null>(null);
  const [qualityRewrite, setQualityRewrite] = useState<{
    title: string;
    description: string;
  } | null>(null);
  const [rewriting, setRewriting] = useState(false);
  const [promoting, setPromoting] = useState(false);
  // Gruppering från AI:n – används för att föreslå uppdelning i flera annonser.
  const [splitInfo, setSplitInfo] = useState<{
    cohesion: "same" | "related" | "mixed";
    groups: SplitGroup[];
  } | null>(null);
  const [highValue, setHighValue] = useState<{
    reason: string;
    low?: number;
    high?: number;
  } | null>(null);
  // Texten kommer från en snabb identifiering – djupvärderingen kan skärpa den.
  const [preliminaryNote, setPreliminaryNote] = useState<string | null>(null);
  // Påbörjad djupvärdering som kan återupptas.
  const [valuationDraft, setValuationDraft] = useState<DeepValuationDraft | null>(null);
  useEffect(() => setValuationDraft(readDeepValuationDraft()), [highValue]);

  // Utloggade får köra hela analysen, men resultatet visas först efter konto.
  // Härlett läge: så länge ingen är inloggad är utkastet låst – även efter
  // omladdning eller när ett sparat utkast hämtas tillbaka.
  const resultLocked = !authLoading && !user && hasDraft && !published;
  // Föremål som väntar på en egen annons efter uppdelning.
  const [pendingGroups, setPendingGroups] = useState<PendingGroup<AdImage>[]>([]);

  const generateAdFn = useServerFn(generateAd);
  const getServerDraftFn = useServerFn(getServerDraft);
  const saveServerDraftFn = useServerFn(saveServerDraft);
  const deleteServerDraftFn = useServerFn(deleteServerDraft);
  const publishAdFn = useServerFn(publishAd);
  const { enabled: testFeatures } = useExpertFeatures();
  const { lang, country: sellCountry } = useLocale();
  // Landet sätts per annons – förvalet är marknaden man surfar i.
  const [adCountry, setAdCountry] = useState<string>(sellCountry);
  useEffect(() => setAdCountry(sellCountry), [sellCountry]);
  // Belopp visas i annonslandets valuta och besökarens sifferformat.
  const moneyLocale = lang === "es" ? "es-ES" : lang === "en" ? "en-GB" : "sv-SE";
  const moneyUnit = adCountry === "ES" ? "€" : "kr";
  const lc = lang === "es" ? {
    createA: "Crea tu", createB: "anuncio", sellA: "Vende tu objeto en", sellB: "segundos", hero: "Fotografía lo que quieres vender y recibe un anuncio completo con precio sugerido. Vende rápido y con buena presentación.", reset: "Borrar y crear un anuncio nuevo", what: "¿Qué quieres vender?", chooseStart: "Elige cómo quieres empezar.", saved: "Tienes un borrador guardado", continue: "Continuar donde lo dejaste", estimate: "Estimación de IA", preliminary: "Texto provisional", deepStarted: "Valoración detallada iniciada", review: "Revisar y ajustar", reanalyse: "Volver a analizar", title: "Título", titlePlaceholder: "Ej. Sofá de terciopelo azul oscuro, 3 plazas", chars: "caracteres", description: "Descripción", descriptionPlaceholder: "Estado, medidas, antigüedad y motivo de venta; bastan unas líneas.", howSell: "¿Cómo quieres venderlo?", fixed: "Precio fijo", fixedText: "Tú fijas el precio. Los compradores contactan contigo y acordáis la venta.", auction: "Subasta", auctionText: "Los compradores pujan. La oferta más alta gana al finalizar.", suggestion: "Nuestra sugerencia", startPrice: "Precio inicial", reserve: "Precio de reserva", lowestBid: "Puja mínima permitida", lowestSell: "Precio mínimo de venta", use: "Usar", duration: "Duración de la subasta", days: "días", price: "Precio", category: "Categoría", chooseCategory: "Elegir categoría", offers: "Aceptar ofertas", offersText: "Los compradores pueden enviar una oferta por mensaje. Tú decides si la aceptas; el precio nunca cambia automáticamente.", locality: "Localidad", locate: "Sugerir localidad desde mi ubicación", login: "Inicia sesión para publicar", publish: "Publicar anuncio", cancel: "Cancelar", startOver: "Empezar de nuevo", deleteImage: "¿Eliminar imagen?",
  } : lang === "en" ? {
    createA: "Create your", createB: "listing", sellA: "Sell your item in", sellB: "seconds", hero: "Photograph what you want to sell and receive a complete listing with a suggested price. Sell quickly and beautifully.", reset: "Clear and create a new listing", what: "What would you like to sell?", chooseStart: "Choose how you would like to begin.", saved: "You have a saved draft", continue: "Continue where you left off", estimate: "AI estimate", preliminary: "Preliminary text", deepStarted: "Detailed valuation started", review: "Review and adjust", reanalyse: "Analyse again", title: "Title", titlePlaceholder: "E.g. dark blue velvet sofa, 3 seats", chars: "characters", description: "Description", descriptionPlaceholder: "Condition, dimensions, age and reason for selling—a few lines are enough.", howSell: "How would you like to sell it?", fixed: "Fixed price", fixedText: "You set the price. Buyers contact you and you agree the sale.", auction: "Auction", auctionText: "Buyers bid against each other. The highest bid wins when time runs out.", suggestion: "Our suggestion", startPrice: "Starting price", reserve: "Reserve price", lowestBid: "Lowest bid someone can place", lowestSell: "Lowest price you will sell for", use: "Use", duration: "Auction duration", days: "days", price: "Price", category: "Category", chooseCategory: "Choose category", offers: "Accept offers", offersText: "Buyers can make an offer in messages. You decide whether to accept; the price never changes automatically.", locality: "Location", locate: "Suggest location from my position", login: "Sign in to publish", publish: "Publish listing", cancel: "Cancel", startOver: "Start over", deleteImage: "Delete image?",
  } : {
    createA: "Skapa din", createB: "annons", sellA: "Sälj din pryl på", sellB: "några sekunder", hero: "Fota det du vill sälja så får du en färdig annonstext och ett prisförslag. Sälj snabbt och snyggt.", reset: "Rensa och skapa en ny annons", what: "Vad vill du sälja?", chooseStart: "Välj hur du vill komma i gång.", saved: "Du har ett sparat utkast", continue: "Fortsätt där du slutade", estimate: "AI-uppskattning", preliminary: "Preliminär text", deepStarted: "Djupvärdering påbörjad", review: "Granska och justera", reanalyse: "Analysera om", title: "Rubrik", titlePlaceholder: "T.ex. Soffa i mörkblå sammet, 3-sits", chars: "tecken", description: "Beskrivning", descriptionPlaceholder: "Skick, mått, ålder och varför du säljer – korta rader räcker.", howSell: "Hur vill du sälja prylen?", fixed: "Fast pris", fixedText: "Du sätter priset. Köpare hör av sig och ni gör upp.", auction: "Auktion", auctionText: "Köpare budar mot varandra. Högsta budet vinner när tiden går ut.", suggestion: "Vårt förslag", startPrice: "Utgångspris", reserve: "Reservationspris", lowestBid: "Lägsta bud någon kan lägga", lowestSell: "Lägsta pris du säljer för", use: "Använd", duration: "Auktionen pågår", days: "dagar", price: "Pris", category: "Kategori", chooseCategory: "Välj kategori", offers: "Ta emot bud", offersText: "Köpare kan lämna ett bud i meddelanden. Du väljer själv om du accepterar eller tackar nej – priset ändras aldrig automatiskt.", locality: "Ort", locate: "Föreslå ort från min plats", login: "Logga in för att publicera", publish: "Publicera annonsen", cancel: "Avbryt", startOver: "Rensa och börja om", deleteImage: "Ta bort bild?",
  };
  // Övriga texter i flödet – dialoger, notiser och listrubriker.
  const lcm = lang === "es" ? {
    fetchingItem: "Recuperando tu artículo…", fetchingItemText: "Añadimos las fotos del escaneo y escribimos tu anuncio. Solo tarda unos segundos.",
    add: "Añadir", ourSuggestion: "Nuestra sugerencia", fetchingDetails: "Recuperando tus datos …",
    personTitle: "Se ve a una persona en la foto.", personText: "Confirmo que todas las personas identificables en las fotos han dado su consentimiento para publicarlas aquí (para menores: consentimiento de su tutor). Asumo toda la responsabilidad de la publicación y eximo a Sellavo de cualquier reclamación. Más información en", personRules: "las reglas de los anuncios",
    publishNow: "Publicar el anuncio directamente",
    latestTitle: "Últimos anuncios", latestText: "Novedades recientes.", seeAll: "Ver todos los anuncios",
    newAdTitle: "¿Empezar un anuncio nuevo?", newAdText: "Se borrarán las fotos, el texto y el precio del anuncio empezado. No se puede deshacer.", keepDraft: "Continuar con el borrador",
    rewriteTitle: "¿Reescribir el texto del anuncio?", rewriteText: "El título, la descripción, el precio sugerido y la categoría actuales se sustituyen por una nueva propuesta. Se perderá lo que hayas escrito.",
    priceTitle: "Primero tenemos que revisar el precio", priceRange: (min: string, max: string) => `Artículos similares suelen estar entre ${min} y ${max}.`, priceHigh: "El precio está muy por encima de lo que aprobamos automáticamente.", priceTail: "Baja el precio o cuéntanos brevemente por qué vale tanto y lo revisamos. El anuncio queda en pausa mientras tanto.", priceMotivationPlaceholder: "Ej. primera edición firmada, con recibo y certificado de autenticidad.",
    restartTitle: "¿Empezar de cero?", restartText: "Se borran las fotos, el texto y el precio para que puedas empezar de nuevo. Te quedas en esta página.",
  } : lang === "en" ? {
    fetchingItem: "Fetching your item…", fetchingItemText: "We bring the photos from the scan and write your listing. It only takes a few seconds.",
    add: "Add", ourSuggestion: "Our suggestion", fetchingDetails: "Fetching your details …",
    personTitle: "A person is visible in the photo.", personText: "I confirm that every identifiable person in the photos has consented to the image being published here (for children: consent from a guardian). I take full responsibility for the publication and hold Sellavo harmless. Read more in", personRules: "the listing rules",
    publishNow: "Publish the listing right away",
    latestTitle: "Latest listings", latestText: "Fresh finds.", seeAll: "See all listings",
    newAdTitle: "Start a new listing?", newAdText: "Photos, text and price in the started listing are cleared. This cannot be undone.", keepDraft: "Keep the draft",
    rewriteTitle: "Rewrite the listing text?", rewriteText: "The current title, description, price suggestion and category are replaced with a new suggestion. What you have written is lost.",
    priceTitle: "We need to look at the price first", priceRange: (min: string, max: string) => `Similar items usually sell for ${min}–${max}.`, priceHigh: "The price is well above what we approve automatically.", priceTail: "Lower the price, or tell us briefly why the item is worth this much and we will take a look. The listing is paused until then.", priceMotivationPlaceholder: "E.g. signed first edition, receipt and certificate of authenticity included.",
    restartTitle: "Start over from the beginning?", restartText: "Photos, text and price are cleared so you can start again. You stay on this page.",
  } : {
    fetchingItem: "Hämtar din pryl…", fetchingItemText: "Vi tar med bilderna från skanningen och skriver din annons. Det tar bara några sekunder.",
    add: "Lägg till", ourSuggestion: "Vårt förslag", fetchingDetails: "Hämtar dina uppgifter …",
    personTitle: "Det syns en person på bilden.", personText: "Jag intygar att alla identifierbara personer på bilderna har lämnat samtycke till att bilden publiceras här (för barn: samtycke från vårdnadshavare). Jag tar fullt ansvar för publiceringen och håller Sellavo skadeslöst. Läs mer i", personRules: "annonsreglerna",
    publishNow: "Publicera annonsen direkt",
    latestTitle: "Senaste annonserna", latestText: "Färska fynd.", seeAll: "Se alla annonser",
    newAdTitle: "Börja med en ny annons?", newAdText: "Bilder, text och pris i den påbörjade annonsen rensas. Det går inte att ångra.", keepDraft: "Behåll utkastet",
    rewriteTitle: "Skriv om annonstexten?", rewriteText: "Nuvarande rubrik, beskrivning, prisförslag och kategori ersätts med ett nytt förslag. Det du har skrivit hittills försvinner.",
    priceTitle: "Vi behöver titta på priset först", priceRange: (min: string, max: string) => `Liknande prylar brukar ligga på ${min}–${max}.`, priceHigh: "Priset ligger högt över vad vi släpper igenom automatiskt.", priceTail: "Sänk priset, eller berätta kort varför prylen är värd så mycket så tittar vi på den. Annonsen ligger pausad tills dess.", priceMotivationPlaceholder: "T.ex. signerad förstautgåva, kvitto och äkthetsbevis finns.",
    restartTitle: "Börja om från början?", restartText: "Bilder, text och pris rensas bort så att du kan börja om. Du stannar kvar här.",
  };
  // Texter för startkortet och utkastfrågorna – samma flöde på alla språk.
  const lcx = lang === "es" ? {
    step1: "Paso 1", shootTitle: "Fotografía tu artículo", shootText: "Haz una foto con la cámara o elige una de tu galería para empezar el anuncio.", startHere: "Hacer una foto", under30: "Menos de 30 segundos", fromLibrary: "Elegir de la galería",
    videoTitle: "Escaneo por vídeo", videoText: "Graba una habitación y encuentra varios artículos a la vez.",
    photosTitle: "Fotografiar varios artículos", photosText: "Sube hasta 30 fotos: las agrupamos por artículo.",
    draftTitle: "Ya tienes un borrador empezado", draftBodyPrefill: "Solo puedes trabajar en un borrador a la vez. ¿Quieres continuar con el que ya tienes o empezar de nuevo con el artículo de Mis artículos? Si empiezas de nuevo, se borrará el anterior.",
    draftKeep: "Continuar con el borrador", draftUseItem: "Usar el artículo",
  } : lang === "en" ? {
    step1: "Step 1", shootTitle: "Photograph your item", shootText: "Take a photo with your camera or choose one from your gallery to start the listing.", startHere: "Take a photo", under30: "Takes less than 30 seconds", fromLibrary: "Choose from library",
    videoTitle: "Video scan", videoText: "Film a room and find several items at once.",
    photosTitle: "Photograph several items", photosText: "Upload up to 30 photos — we group them per item.",
    draftTitle: "You already have a draft in progress", draftBodyPrefill: "You can only work on one draft at a time. Continue with the draft you have, or start over with the item from My items? Starting over deletes the old draft.",
    draftKeep: "Continue the draft", draftUseItem: "Use the item",
  } : {
    step1: "Steg 1", shootTitle: "Fota din pryl", shootText: "Ta en bild med kameran eller välj en från galleriet för att påbörja annonsen.", startHere: "Ta en bild", under30: "Tar mindre än 30 sekunder", fromLibrary: "Välj från biblioteket",
    videoTitle: "Videoskanning", videoText: "Filma ett rum och hitta flera prylar på en gång.",
    photosTitle: "Fota flera prylar", photosText: "Ladda upp upp till 30 bilder — vi grupperar per pryl.",
    draftTitle: "Du har redan ett påbörjat utkast", draftBodyPrefill: "Du kan bara jobba med ett utkast i taget. Vill du fortsätta på utkastet du redan har, eller börja om med prylen från Mina prylar? Börjar du om raderas det gamla utkastet.",
    draftKeep: "Fortsätt på utkastet", draftUseItem: "Använd prylen",
  };
  const flowText = lang === "es" ? {
    checking: "Comprobando tus datos", reviewing: "Revisando título y descripción", reviewingPrice: "Revisando el precio", uploading: "Subiendo, traduciendo y publicando", translating: "Traduciendo y publicando el anuncio",
    titleMissing: "El título está vacío.", descriptionMissing: "La descripción está vacía.", priceMissing: "Establece un precio para el anuncio.", categoryMissing: "Elige una categoría.", locationMissing: "Elige una localidad de la lista o usa una dirección guardada.", locationHelp: "Elige una localidad de la lista o usa una dirección guardada. Si no encuentras la tuya, elige la más cercana.", consentMissing: "Confirma el consentimiento de la persona que aparece en la foto.", consentHelp: "Confirma que tienes el consentimiento de la persona que aparece en la foto o cambia la imagen.", termsMissing: "Acepta los términos de uso.", quota: "Has alcanzado el límite de anuncios.", plans: "Ver planes", published: "¡Tu anuncio ha sido publicado!", imageAlt: "Vista previa del objeto que vendes", rotate: "Girar imagen", deleteDescription: "¿Quieres eliminar esta imagen? No se puede deshacer.", remove: "Eliminar",
  } : lang === "en" ? {
    checking: "Checking your details", reviewing: "Reviewing title and description", reviewingPrice: "Reviewing the price", uploading: "Uploading, translating and publishing", translating: "Translating and publishing the listing",
    titleMissing: "The title is empty.", descriptionMissing: "The description is empty.", priceMissing: "Set a price for the listing.", categoryMissing: "Choose a category.", locationMissing: "Choose a location from the list or use a saved address.", locationHelp: "Choose a location from the list or use a saved address. If yours is missing, choose the nearest one.", consentMissing: "Confirm consent for the person in the photo.", consentHelp: "Confirm that you have consent from the person in the photo, or replace the image.", termsMissing: "Accept the terms of use.", quota: "You have reached your listing limit.", plans: "View plans", published: "Your listing is published!", imageAlt: "Preview of the item you are selling", rotate: "Rotate image", deleteDescription: "Do you want to delete this image? This cannot be undone.", remove: "Delete",
  } : {
    checking: "Kontrollerar dina uppgifter", reviewing: "Granskar rubrik och beskrivning", reviewingPrice: "Granskar priset", uploading: "Laddar upp, översätter och publicerar", translating: "Översätter och publicerar annonsen",
    titleMissing: "Rubriken är tom.", descriptionMissing: "Beskrivningen är tom.", priceMissing: "Sätt ett pris på annonsen.", categoryMissing: "Välj en kategori.", locationMissing: "Välj en ort i listan eller använd en sparad adress.", locationHelp: "Välj en ort i listan eller använd en sparad adress. Hittar du inte din ort, välj den närmaste.", consentMissing: "Intyga samtycke för personen på bilden.", consentHelp: "Intyga att du har samtycke från personen på bilden, eller byt bild.", termsMissing: "Godkänn användarvillkoren.", quota: "Din annonsgräns är nådd.", plans: "Se planer", published: "Din annons är publicerad!", imageAlt: "Förhandsvisning av prylen du säljer", rotate: "Rotera bilden", deleteDescription: "Vill du ta bort den här bilden? Det går inte att ångra.", remove: "Ta bort",
  };
  const checkAdPriceFn = useServerFn(checkAdPrice);
  const promoteAdFn = useServerFn(promoteAd);
  const [draftSavedAt, setDraftSavedAt] = useState<number | null>(null);

  // Återställ sparat utkast (t.ex. efter inloggning/e-postbekräftelse).
  // Vi väntar in sessionen: utkastet är personligt och får bara återställas
  // för den som äger det (utkast påbörjade utloggad adopteras vid inloggning).
  useEffect(() => {
    if (authLoading) return;
    let cancelled = false;
    void (async () => {
      let d = await loadDraft<DraftForm, AdImage>(user?.id ?? null);
      // Inget lokalt utkast men inloggad? Hämta hem det som sparades vid registreringen.
      if (!d?.images?.length && user) {
        try {
          const remote = await getServerDraftFn({});
          if (remote?.images?.length) {
            d = {
              form: (remote.payload?.form ?? {}) as DraftForm,
              images: remote.images as AdImage[],
              video: remote.payload?.video ?? null,
              tags: remote.payload?.tags ?? [],
              savedAt: remote.payload?.savedAt ?? Date.now(),
            };
          }
        } catch {
          // Utkastet är inte kritiskt – flödet fungerar ändå.
        }
      }
      if (!cancelled && d?.images?.length && d.form) {
        setForm({ ...emptyForm, ...d.form });
        setSaleKind(d.form.saleKind === "auction" ? "auction" : "fixed");
        setReservePrice(d.form.reservePrice ?? "");
        setAuctionDays(
          d.form.auctionDays === "3" || d.form.auctionDays === "14"
            ? (Number(d.form.auctionDays) as 3 | 14)
            : 7,
        );
        setPeopleInImages(d.form.peopleInImages === "true");
        setPeopleConsent(d.form.peopleConsent === "true");
        setAccepted(d.form.accepted === "true");
        setSaleAdvice(
          d.form.saleAdviceKind === "fixed" || d.form.saleAdviceKind === "auction"
            ? { kind: d.form.saleAdviceKind, reason: d.form.saleAdviceReason ?? "" }
            : null,
        );
        const restoredValuationLow = Number(d.form.valuationLow);
        const restoredValuationHigh = Number(d.form.valuationHigh);
        setAiValuation(
          restoredValuationLow > 0
            ? {
                low: restoredValuationLow,
                high: restoredValuationHigh > 0 ? restoredValuationHigh : null,
              }
            : null,
        );
        setPriceMotivation(d.form.priceMotivation ?? "");
        setHiddenAd(d.form.hidden === "true");
        setImages(d.images);
        if (d.studio) {
          const toNumKeyed = (rec: Record<string, AdImage>) => {
            const out: Record<number, { imageBase64: string; mimeType: string }> = {};
            for (const [k, v] of Object.entries(rec ?? {})) {
              const n = Number(k);
              if (Number.isFinite(n) && v)
                out[n] = { imageBase64: v.imageBase64, mimeType: v.mimeType };
            }
            return out;
          };
          setCleanOriginals(toNumKeyed(d.studio.originals as Record<string, AdImage>));
          setCleanResults(
            Object.fromEntries(
              Object.entries((d.studio.results ?? {}) as Record<string, AdImage>).map(([k, v]) => [
                k,
                { imageBase64: v.imageBase64, mimeType: v.mimeType },
              ]),
            ),
          );
          setCleanVariants(d.studio.variants ?? {});
          const modes: Record<number, CleanMode> = {};
          for (const [k, v] of Object.entries(d.studio.modes ?? {})) {
            const n = Number(k);
            if (Number.isFinite(n) && (v === "background" || v === "studio" || v === "angle")) {
              modes[n] = v;
            }
          }
          setCleanMode(modes);
        }
        if (d.video?.path)
          setVideo({ path: d.video.path, name: d.video.name, poster: d.video.poster ?? null });
        if (d.pendingGroups?.length) setPendingGroups(d.pendingGroups);
        if (d.tags?.length) setAiTags(d.tags);

        // Bilder/material är ett sparat arbetsläge, inte en färdig annons.
        // Endast ett utkast med faktiskt genererad annonstext får visa resultatet.
        setHasDraft(Boolean(d.form.title?.trim() && d.form.description?.trim()));
        setDraftSavedAt(d.savedAt ?? null);
        // Återställ exakt var användaren befann sig. Ett sparat utkast ska
        // aldrig flytta användaren till ett annat steg eller starta något.
        let savedScroll = 0;
        try {
          savedScroll = Number(window.sessionStorage.getItem(SELL_FLOW_SCROLL_KEY) ?? 0);
        } catch {
          // Scrollåterställning är bara en bekvämlighet.
        }
        if (savedScroll > 0) {
          window.setTimeout(() => window.scrollTo({ top: savedScroll, behavior: "instant" }), 80);
        }
      }
      if (!cancelled) setRestored(true);
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authLoading, user?.id]);

  // Så fort kontot finns låser vi upp resultatet – utkastet ligger kvar.
  const wasLockedRef = useRef(false);
  useEffect(() => {
    if (resultLocked) {
      wasLockedRef.current = true;
      return;
    }
    if (user && wasLockedRef.current) {
      wasLockedRef.current = false;
      track("ai_unlocked");
      toast.success("Välkommen! Här är din annons – granska och publicera.");
      setPendingScroll(true);
    }
  }, [user, resultLocked]);

  // Har flödet någon gång haft bilder? Då betyder "noll bilder" att användaren
  // tagit bort dem – och då ska det sparade utkastet också bort.
  const hadImagesRef = useRef(false);

  // Spara utkast löpande så att inget försvinner vid inloggning
  useEffect(() => {
    if (!restored) return;
    if (published && !pendingGroups.length) {
      void clearDraft();
      return;
    }
    if (images.length) {
      hadImagesRef.current = true;
    } else {
      // Tog användaren bort sista bilden får inte det gamla utkastet ligga kvar.
      if (hadImagesRef.current && !pendingGroups.length) {
        hadImagesRef.current = false;
        setHasDraft(false);
        void clearDraft();
      }
      return;
    }
    const t = window.setTimeout(() => {
      void saveDraft(
        {
          ...form,
          saleKind,
          reservePrice,
          auctionDays: String(auctionDays) as DraftForm["auctionDays"],
          peopleInImages: String(peopleInImages) as DraftForm["peopleInImages"],
          peopleConsent: String(peopleConsent) as DraftForm["peopleConsent"],
          accepted: String(accepted) as DraftForm["accepted"],
          saleAdviceKind: saleAdvice?.kind ?? "",
          saleAdviceReason: saleAdvice?.reason ?? "",
          valuationLow: aiValuation ? String(aiValuation.low) : "",
          valuationHigh: aiValuation?.high ? String(aiValuation.high) : "",
          priceMotivation,
          hidden: String(hiddenAd) as DraftForm["hidden"],
        },
        images,
        video,
        pendingGroups,
        user?.id ?? null,
        aiTags,
        studioDraftRef.current,
      );
    }, 400);
    return () => window.clearTimeout(t);
  }, [
    form,
    images,
    video,
    published,
    restored,
    pendingGroups,
    user?.id,
    aiTags,
    saleKind,
    reservePrice,
    auctionDays,
    peopleInImages,
    peopleConsent,
    accepted,
    saleAdvice,
    aiValuation,
    priceMotivation,
    hiddenAd,
  ]);

  // Kom ihåg positionen löpande. Då fungerar även webbläsarens egen
  // Tillbaka-knapp och länkar som inte tillhör uppgraderingsflödet.
  useEffect(() => {
    if (!restored || variant !== "create") return;
    let frame = 0;
    const rememberPosition = () => {
      window.cancelAnimationFrame(frame);
      frame = window.requestAnimationFrame(() => {
        try {
          window.sessionStorage.setItem(SELL_FLOW_SCROLL_KEY, String(window.scrollY));
        } catch {
          // Scrollpositionen är inte kritisk för själva utkastet.
        }
      });
    };
    window.addEventListener("scroll", rememberPosition, { passive: true });
    return () => {
      window.removeEventListener("scroll", rememberPosition);
      window.cancelAnimationFrame(frame);
      try {
        window.sessionStorage.setItem(SELL_FLOW_SCROLL_KEY, String(window.scrollY));
      } catch {
        // Utkastet finns fortfarande kvar i IndexedDB.
      }
    };
  }, [restored, variant]);

  // Lämnar man flödet (t.ex. till priser) sparar vi direkt – inget får hänga
  // på den fördröjda autosparningen.
  const saveBeforeLeaving = () => {
    rememberReturn(returPath);
    try {
      window.sessionStorage.setItem(SELL_FLOW_SCROLL_KEY, String(window.scrollY));
    } catch {
      // Utkastet sparas fortfarande även om sessionslagring är blockerad.
    }
    if (!images.length || published) return;
    void saveDraft(
      {
        ...form,
        saleKind,
        reservePrice,
        auctionDays: String(auctionDays) as DraftForm["auctionDays"],
        peopleInImages: String(peopleInImages) as DraftForm["peopleInImages"],
        peopleConsent: String(peopleConsent) as DraftForm["peopleConsent"],
        accepted: String(accepted) as DraftForm["accepted"],
        saleAdviceKind: saleAdvice?.kind ?? "",
        saleAdviceReason: saleAdvice?.reason ?? "",
        valuationLow: aiValuation ? String(aiValuation.low) : "",
        valuationHigh: aiValuation?.high ? String(aiValuation.high) : "",
        priceMotivation,
        hidden: String(hiddenAd) as DraftForm["hidden"],
      },
      images,
      video,
      pendingGroups,
      user?.id ?? null,
      aiTags,
      studioDraftRef.current,
    );
  };

  const hadServerDraftRef = useRef(false);

  // Serverkopia av utkastet för inloggade – finns kvar även om webbläsaren rensas.
  useEffect(() => {
    if (!restored || !user) return;
    if (published && !pendingGroups.length) {
      void deleteServerDraftFn({}).catch(() => undefined);
      return;
    }
    if (!images.length) {
      // Sista bilden borttagen – ta bort serverkopian också.
      if (hadServerDraftRef.current && !pendingGroups.length) {
        hadServerDraftRef.current = false;
        void deleteServerDraftFn({}).catch(() => undefined);
      }
      return;
    }
    hadServerDraftRef.current = true;
    const t = window.setTimeout(() => {
      void saveServerDraftFn({
        data: {
          payload: {
            form: {
              ...form,
              saleKind,
              reservePrice,
              auctionDays: String(auctionDays),
              peopleInImages: String(peopleInImages),
              peopleConsent: String(peopleConsent),
              accepted: String(accepted),
              saleAdviceKind: saleAdvice?.kind ?? "",
              saleAdviceReason: saleAdvice?.reason ?? "",
              valuationLow: aiValuation ? String(aiValuation.low) : "",
              valuationHigh: aiValuation?.high ? String(aiValuation.high) : "",
              priceMotivation,
          hidden: String(hiddenAd) as DraftForm["hidden"],
            } as Record<string, string>,
            video: video ?? null,
            tags: aiTags,
            savedAt: Date.now(),
          },
          images: images.map((i) => ({ imageBase64: i.imageBase64, mimeType: i.mimeType })),
        },
      }).catch(() => undefined);
    }, 2500);
    return () => window.clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    form,
    images,
    video,
    published,
    restored,
    user?.id,
    aiTags,
    saleKind,
    reservePrice,
    auctionDays,
    peopleInImages,
    peopleConsent,
    accepted,
    saleAdvice,
    aiValuation,
    priceMotivation,
    hiddenAd,
  ]);

  // Utan konto sparas ingenting – varna innan man lämnar sidan och rensa utkastet.
  const unsavedDraft = !user && !authLoading && images.length > 0 && !published;
  // Inom sajten varnar vi aldrig – utkastet ligger kvar och den flytande
  // knappen tar en tillbaka hit. Bara när fliken stängs eller man lämnar
  // domänen visar webbläsaren sin egen varning.
  useBlocker({
    shouldBlockFn: () => false,
    enableBeforeUnload: () => unsavedDraft,
  });

  // Hämta sparade kontaktuppgifter från kontot (senaste annonsen) och fyll i det som saknas
  const planStatusFn = useServerFn(getMyPlanStatus);
  const { data: planStatus } = useQuery({
    queryKey: ["my-plan"],
    queryFn: () => planStatusFn({}),
    enabled: Boolean(user),
  });
  const quotaBlocked = planStatus ? !planStatus.quota.allowed : false;
  const [hydrated, setHydrated] = useState(false);
  useEffect(() => setHydrated(true), []);
  const activePlan: PlanId = (planStatus?.plan as PlanId | undefined) ?? "free";
  const planLimits = PLANS[hydrated ? activePlan : "free"].limits;
  // "Kom igång" ska alltid fråga hur man vill sälja – samma val som på Mina
  // prylar. Lägen som planen inte täcker leder till priser, och är man inte
  // inloggad öppnas inloggningen för de lägen som kräver konto.
  const showModeChooser = variant === "home";
  const maxImages = planLimits.maxImages;
  const canVideo = planLimits.video;
  const promotionStatusFn = useServerFn(getPromotionStatus);
  const { data: promotionStatus, refetch: refetchPromotions } = useQuery({
    queryKey: ["promotion-status"],
    queryFn: () => promotionStatusFn({}),
    enabled: Boolean(user),
  });
  // Superadmin kan publicera annonser som är dolda för alla andra.
  const myRoleFn = useServerFn(getMyRole);
  const { data: myRole } = useQuery({
    queryKey: ["my-role"],
    queryFn: () => myRoleFn({}),
    enabled: Boolean(user),
    staleTime: 5 * 60_000,
  });
  const isSuperadmin = myRole?.role === "superadmin";

  const contactDefaultsFn = useServerFn(getMyContactDefaults);
  const { data: contactDefaults, isPending: contactDefaultsPending } = useQuery({
    queryKey: ["contact-defaults", user?.id],
    queryFn: () => contactDefaultsFn({}),
    enabled: !!user,
    staleTime: 30 * 1000,
    refetchOnMount: "always",
    refetchOnWindowFocus: true,
  });

  useEffect(() => {
    if (!user) return;
    // Vänta tills profilen faktiskt hämtats – annars nollställs ett befintligt
    // visningsnamn medan anropet pågår och rutan "välj visningsnamn" blinkar upp.
    if (!contactDefaults) {
      setForm((f) => ({
        ...f,
        contactEmail: f.contactEmail || (user.email as string) || "",
      }));
      return;
    }
    setForm((f) => ({
      ...f,
      contactName: contactDefaults.contactName || f.contactName,
      contactEmail: contactDefaults.contactEmail || (user.email as string) || "",
      contactPhone: contactDefaults.contactPhone ?? "",
    }));
  }, [user, contactDefaults]);

  // Har man en sparad adress ska orten redan vara ifylld – man ska slippa välja
  // samma ort varje gång. Vi markerar tydligt att den är förvald och går att ändra.
  const savedPlacesFn = useServerFn(listSavedPlaces);
  const { data: savedPlaces } = useQuery({
    queryKey: ["saved-places"],
    queryFn: () => savedPlacesFn({}),
    enabled: !!user,
    staleTime: 5 * 60_000,
  });

  useEffect(() => {
    const places = savedPlaces ?? [];
    if (!places.length) return;
    const preferred = places.find((p) => p.is_default) ?? places[0];
    if (!preferred?.location) return;
    setForm((f) => {
      if (f.location) return f;
      setLocationPrefilled(true);
      return { ...f, location: preferred.location, address: f.address || preferred.address || "" };
    });
  }, [savedPlaces]);

  // Saknar man sparad adress kan vi räkna ut orten från enhetens position.
  const suggestLocalityFn = useServerFn(suggestLocalityFromPoint);
  const [geoState, setGeoState] = useState<{
    status: "idle" | "loading" | "done" | "error";
    message?: string;
  }>({ status: "idle" });

  const applyPoint = useCallback(
    async (latitude: number, longitude: number) => {
      try {
        const res = await suggestLocalityFn({ data: { latitude, longitude, country: adCountry } });
        if (res.location) {
          setLocationPrefilled(false);
          setForm((f) => ({ ...f, location: res.location as string }));
          setGeoState({ status: "done", message: `Ort satt utifrån din plats: ${res.location}` });
        } else {
          setGeoState({
            status: "error",
            message: res.raw
              ? `Vi hittade området ”${res.raw}”. Välj närmaste ort i listan.`
              : "Vi kunde inte hitta någon ort för din plats.",
          });
        }
      } catch {
        setGeoState({ status: "error", message: "Vi kunde inte hämta din ort just nu." });
      }
    },
    [adCountry, suggestLocalityFn],
  );

  const permissionPrimer = usePermissionPrimer();
  const askPermission = permissionPrimer.ask;

  const useMyPosition = useCallback(() => {
    setGeoState({ status: "loading" });
    // I appen frågar vi telefonen direkt – annars webbläsarens platstjänst.
    void (async () => {
      const { isAppMode, getNativePosition } = await import("@/app-bridge/core/wrapper");
      // Förklara varför innan telefonens egen platsfråga dyker upp.
      if (isAppMode() && !(await askPermission("location"))) {
        setGeoState({ status: "idle" });
        return;
      }
      if (isAppMode()) {
        const pos = await getNativePosition();
        if (pos) {
          await applyPoint(pos.latitude, pos.longitude);
          return;
        }
      }
      if (typeof navigator === "undefined" || !("geolocation" in navigator)) {
        setGeoState({ status: "error", message: "Din enhet stödjer inte platsdelning." });
        return;
      }
      navigator.geolocation.getCurrentPosition(
        (pos) => void applyPoint(pos.coords.latitude, pos.coords.longitude),
        () => setGeoState({ status: "error", message: "Vi fick inte tillgång till din plats." }),
        { enableHighAccuracy: false, timeout: 10000, maximumAge: 600000 },
      );
    })();
  }, [applyPoint, askPermission]);



  // Annonslistan hämtas i <LatestAdsGrid> så att sidan kan strömma ut direkt.

  const processFiles = useCallback(
    async (files: File[], replaceAll = false) => {
      if (!files.length) return;

      const replaceIndex = replaceAll ? null : replaceImageIndexRef.current;
      replaceImageIndexRef.current = null;
      let selectedFiles = replaceIndex === null ? files : files.slice(0, 1);
      const currentImageCount = replaceAll ? 0 : images.length;

      // Fler bilder än planen tillåter ska aldrig tyst försvinna – säg till.
      if (replaceIndex === null) {
        const room = Math.max(0, maxImages - currentImageCount);
        if (room === 0) {
          toast.error(
            activePlan === "free"
              ? `Gratis har ${maxImages} bilder per annons. Uppgradera till Plus för 6 bilder – eller ta bort en bild för att byta.`
              : `Din nivå tillåter max ${maxImages} bilder per annons. Ta bort en bild innan du lägger till en ny.`,
          );
          if (fileInputRef.current) fileInputRef.current.value = "";
          return;
        }
        if (selectedFiles.length > room) {
          toast.warning(
            activePlan === "free"
              ? `Gratis har ${maxImages} bilder per annons – vi tog med de ${room} första. Uppgradera till Plus för 6 bilder.`
              : `Din nivå tillåter max ${maxImages} bilder per annons. Vi tog med de ${room} första – resten hoppades över.`,
          );
          selectedFiles = selectedFiles.slice(0, room);
        }
      }

      setError(null);
      setPublished(null);

      // Samma bildinläsning som resten av sajten (HEIC → JPEG, komprimering).
      const normalize = (file: File) => normalizeImageFile(file);

      const read = (file: Blob) =>
        new Promise<AdImage>((resolve) => {
          const reader = new FileReader();
          reader.onloadend = () =>
            resolve({
              imageBase64: (reader.result as string).split(",")[1],
              mimeType: file.type || "image/jpeg",
            });
          reader.readAsDataURL(file);
        });

      const added: AdImage[] = [];
      let failed = 0;
      const total =
        replaceIndex === null
          ? Math.min(selectedFiles.length, maxImages - currentImageCount) || selectedFiles.length
          : selectedFiles.length;
      setUploadProgress({ done: 0, total, pct: 4 });
      for (let i = 0; i < selectedFiles.length; i++) {
        setUploadProgress({ done: i, total, pct: Math.round(((i + 0.35) / total) * 100) });
        try {
          const blob = await normalize(selectedFiles[i]);
          setUploadProgress({ done: i, total, pct: Math.round(((i + 0.75) / total) * 100) });
          added.push(await read(blob));
        } catch {
          failed++;
        }
        setUploadProgress({ done: i + 1, total, pct: Math.round(((i + 1) / total) * 100) });
      }
      setUploadProgress({ done: total, total, pct: 100 });
      window.setTimeout(() => setUploadProgress(null), 500);

      if (added.length) {
        if (replaceIndex === null) {
          setImages((prev) =>
            (replaceAll ? added : [...prev, ...added]).slice(0, maxImages),
          );
        } else {
          setImages((prev) =>
            prev.map((image, index) => (index === replaceIndex ? added[0] : image)),
          );
          setPhotoQuality(null);
          setAiInsight(null);
          setForm((current) => ({ ...current, title: "", description: "", price: "" }));
          setHasDraft(false);
          setAiTags([]);
          setSplitInfo(null);
          setContactFinding(null);
        }
      }
      if (failed) {
        setError(
          added.length
            ? `${failed} bild${failed > 1 ? "er" : ""} kunde inte läsas. Prova att spara om som JPG eller PNG.`
            : "Kunde inte läsa bilden. Prova att ladda upp den som JPG eller PNG.",
        );
      }
      if (fileInputRef.current) fileInputRef.current.value = "";
    },
    [images.length, maxImages, activePlan],
  );

  const handleFileChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      void processFiles(Array.from(e.target.files ?? []));
    },
    [processFiles],
  );

  /** I appen används telefonens kamera; i webben öppnas den vanliga filväljaren. */
  const openCamera = useCallback(
    (replaceIndex: number | null = null) => {
      replaceImageIndexRef.current = replaceIndex;
      if (!isAppMode()) {
        fileInputRef.current?.click();
        return;
      }
      void import("@/app-bridge/core/wrapper").then(async (m) => {
        const photo = await m.takeNativePhoto();
        if (photo) {
          void processFiles([photo]);
          return;
        }
        fileInputRef.current?.click();
      });
    },
    [processFiles],
  );

  /** I appen används bildbiblioteket direkt; annars webbens filväljare. */
  const openLibrary = useCallback(
    (replaceIndex: number | null = null) => {
      replaceImageIndexRef.current = replaceIndex;
      if (!isAppMode()) {
        fileInputRef.current?.click();
        return;
      }
      void import("@/app-bridge/core/wrapper").then(async (m) => {
        const photos = await m.pickNativePhotos({ multiple: replaceIndex === null });
        if (photos?.length) {
          void processFiles(photos);
          return;
        }
        fileInputRef.current?.click();
      });
    },
    [processFiles],
  );

  const resetForNewImages = useCallback(async () => {
    await clearDraft();
    if (user) void deleteServerDraftFn({}).catch(() => undefined);
    setImages([]);
    clearStudio();
    setVideo(null);
    setPendingGroups([]);
    setForm(emptyForm);
    setSaleKind("fixed");
    setReservePrice("");
    setAuctionDays(7);
    setPeopleInImages(false);
    setPeopleConsent(false);
    setAccepted(false);
    setSaleAdvice(null);
    setAiValuation(null);
    setPriceMotivation("");
    setAiTags([]);
    setSplitInfo(null);
    setPublished(null);
    setHasDraft(false);
    setDraftSavedAt(null);
    hadImagesRef.current = false;
    hadServerDraftRef.current = false;
  }, [deleteServerDraftFn, user]);

  // En bild som valts från startsidan betyder alltid "ny annons". Det gamla
  // utkastet rensas direkt, utan konfliktfråga, varning eller omväg tillbaka.
  useEffect(() => {
    if (variant !== "create" || !restored) return;
    const pending = takePendingAdFiles();
    if (!pending.length) return;
    void (async () => {
      await resetForNewImages();
      await processFiles(pending, true);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [variant, restored]);

  const acceptHomeFiles = useCallback(
    async (files: File[]) => {
      if (!files.length) return;
      setPendingAdFiles(files);
      await navigate({ to: "/skapa" });
    },
    [navigate],
  );

  const handleHomeFileChange = useCallback(
    async (input: HTMLInputElement) => {
      const files = Array.from(input.files ?? []);
      input.value = "";
      await acceptHomeFiles(files);
    },
    [acceptHomeFiles],
  );

  const videoInputRef = useRef<HTMLInputElement>(null);
  const videoCardRef = useRef<HTMLDivElement>(null);
  const online = useOnline();
  const [videoUploading, setVideoUploading] = useState(false);
  const [videoStatus, setVideoStatus] = useState<string | null>(null);
  const createVideoUploadFn = useServerFn(createVideoUpload);

  // Publicering och filmuppladdning kan ta en stund – i appen ber vi
  // telefonen hålla skärmen vaken så att flödet inte pausas halvvägs.
  useEffect(() => {
    if (!publishing && !videoUploading) return;
    let release = () => undefined as void;
    let alive = true;
    void import("@/app-bridge/core/wrapper").then((m) => {
      if (alive) release = m.retainNativeKeepAwake();
    });
    return () => {
      alive = false;
      release();
    };
  }, [publishing, videoUploading]);

  const processVideoFile = useCallback(
    async (file: File) => {
      setError(null);
      setVideoUploading(true);
      setVideoStatus("Gör videon redo...");
      try {
        const { prepareVideoForWeb } = await import("@/lib/video-compress");
        const prepared = await prepareVideoForWeb(file, (p) =>
          setVideoStatus(`Optimerar videon... ${p}%`),
        );

        setVideoStatus("Granskar ljudet i videon...");
        const { extractAudioBase64 } = await import("@/lib/video-audio");
        const audioBase64 = await extractAudioBase64(prepared.blob);
        const { checkAdAudio } = await import("@/lib/audio-moderation.functions");
        const audio = await checkAdAudio({
          data: audioBase64
            ? { audioBase64, mimeType: "audio/wav" }
            : { mimeType: "audio/wav", silent: true },
        });
        if (!audio.ok) {
          const { issuesToMessage } = await import("@/lib/moderation");
          throw new Error(
            `Ljudet i videon innehåller kontaktuppgifter. ${issuesToMessage(audio.issues)}`,
          );
        }
        if (!audio.clearanceId) throw new Error("Kunde inte granska ljudet. Försök igen.");

        setVideoStatus("Laddar upp videon...");

        const { path, token } = await createVideoUploadFn({
          data: {
            mimeType: prepared.mimeType,
            sizeBytes: prepared.blob.size,
            audioClearanceId: audio.clearanceId,
          },
        });

        const { supabase } = await import("@/integrations/supabase/client");
        const { error: upErr } = await supabase.storage
          .from("ad-images")
          .uploadToSignedUrl(path, token, prepared.blob, { contentType: prepared.mimeType });
        if (upErr) throw new Error(upErr.message);
        const { extractVideoPoster } = await import("@/lib/video-poster");
        let poster = await extractVideoPoster(
          new File([prepared.blob], `video.${prepared.ext}`, { type: prepared.mimeType }),
        );
        if (!poster) {
          // Lyckas vi inte läsa filen lokalt provar vi den uppladdade filmen istället,
          // så att annonsen alltid får en stillbild.
          const { adVideoSrc } = await import("@/lib/video-src");
          poster = await extractVideoPoster(adVideoSrc(path));
        }
        setVideo({ path, name: prepared.label, poster });
        // Visa resultatet direkt om videorutan hamnat utanför skärmen.
        window.setTimeout(() => {
          const el = videoCardRef.current;
          if (!el) return;
          const rect = el.getBoundingClientRect();
          const outside = rect.top < 80 || rect.bottom > window.innerHeight;
          if (outside) el.scrollIntoView({ behavior: "smooth", block: "nearest" });
        }, 120);
      } catch (err) {
        setError(err instanceof Error ? err.message : "Kunde inte ladda upp videon.");
      } finally {
        setVideoUploading(false);
        setVideoStatus(null);
      }
    },
    [createVideoUploadFn],
  );

  const handleVideoChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (videoInputRef.current) videoInputRef.current.value = "";
      if (file) void processVideoFile(file);
    },
    [processVideoFile],
  );

  const [rotating, setRotating] = useState<number | null>(null);

  /** AI-rensning av bakgrunden – originalet sparas så att man kan ångra. */
  const cleanImageFn = useServerFn(cleanImage);
  const [cleaning, setCleaning] = useState<number | null>(null);
  const [cleanOriginals, setCleanOriginals] = useState<
    Record<number, { imageBase64: string; mimeType: string }>
  >({});
  const [cleanQuota, setCleanQuota] = useState<CleanImageQuota | null>(null);
  /** Antal varianter per bild och läge, t.ex. "0:studio". */
  const [cleanVariants, setCleanVariants] = useState<Record<string, number>>({});
  /** Valt läge per bild: bara bakgrund, full uppsnyggning eller ny vinkel. */
  const [cleanMode, setCleanMode] = useState<Record<number, CleanMode>>({});
  const [cleanCached, setCleanCached] = useState(false);
  /** Varning när AI:n verkar ha ändrat själva prylen. */
  const [cleanWarning, setCleanWarning] = useState<string | null>(null);
  /** Öppen bildstudio-dialog för bildindex. */
  const [cleanDialog, setCleanDialog] = useState<number | null>(null);
  /** Lokalt sparade rensningar: "index:mode:variant" → färdig bild. Återanvänds direkt. */
  const [cleanResults, setCleanResults] = useState<
    Record<string, { imageBase64: string; mimeType: string }>
  >({});
  /** Steg 0: vad AI:n läste av i bilden (pryl, skräp och ruta) per bildindex. */
  const [studioInfo, setStudioInfo] = useState<Record<number, StudioSubject | null>>({});
  const [analyzingStudio, setAnalyzingStudio] = useState<number | null>(null);
  /**
   * Vald version i bildstudion: "index:läge:variant", eller null för originalet.
   * Selection är bara en förhandsvisning – annonsbilden ändras först vid "Använd bilden".
   */
  const [studioSelection, setStudioSelection] = useState<Record<number, string | null>>({});
  /** Sant när studion har en vald version som inte skrivits in i annonsen än. */
  const [studioDirty, setStudioDirty] = useState<Record<number, boolean>>({});
  /** Notis när en ny variant inte blev bättre än den förra. */
  const [variantNote, setVariantNote] = useState<string | null>(null);

  /** Flyttar/rensar all studiodata när bilder tas bort eller byter plats. */
  const remapStudio = useCallback((map: IndexMap) => {
    setCleanOriginals((prev) => remapNumKeyed(prev, map));
    setCleanMode((prev) => remapNumKeyed(prev, map));
    setStudioInfo((prev) => remapNumKeyed(prev, map));
    setCleanVariants((prev) => remapStrKeyed(prev, map));
    setCleanResults((prev) => remapStrKeyed(prev, map));
    // Valet är bara en förhandsvisning – det räknas om nästa gång studion öppnas.
    setStudioSelection({});
    setStudioDirty({});
  }, []);

  /** Nollställer bildstudion helt (nytt utkast, börja om, efter publicering). */
  const clearStudio = useCallback(() => {
    setCleanOriginals({});
    setCleanMode({});
    setCleanVariants({});
    setCleanResults({});
    setStudioInfo({});
    setStudioSelection({});
    setStudioDirty({});
    setVariantNote(null);

    setCleanDialog(null);
    setCleanDebug(null);
    setCleanCached(false);
    studioDraftRef.current = { originals: {}, results: {}, variants: {}, modes: {} };
  }, []);

  // Håll utkastets studiodata uppdaterad så originalet alltid går att ångra,
  // även efter omladdning eller inloggning. Poster för borttagna bilder rensas.
  useEffect(() => {
    const count = images.length;
    const inRange = (k: string) => {
      const n = Number(k.split(":")[0]);
      return Number.isFinite(n) && n >= 0 && n < count;
    };
    const pick = <T,>(rec: Record<string, T>) =>
      Object.fromEntries(Object.entries(rec).filter(([k]) => inRange(k)));
    studioDraftRef.current = {
      originals: pick(cleanOriginals as unknown as Record<string, AdImage>),
      results: pick(cleanResults),
      variants: pick(cleanVariants),
      modes: pick(Object.fromEntries(Object.entries(cleanMode).map(([k, v]) => [k, v as string]))),
    };
  }, [cleanOriginals, cleanResults, cleanVariants, cleanMode, images.length]);

  const [cleanDebug, setCleanDebug] = useState<{
    model: string;
    inputTokens: number;
    outputTokens: number;
    credits: number;
    durationMs: number;
  } | null>(null);

  const quotaFn = useServerFn(getCleanImageQuota);
  useEffect(() => {
    if (!user) {
      setCleanQuota(null);
      return;
    }
    let alive = true;
    void quotaFn()
      .then((q) => {
        if (alive) setCleanQuota(q);
      })
      .catch(() => undefined);
    return () => {
      alive = false;
    };
  }, [user, quotaFn]);

  /**
   * Steg 0 i bildstudion: AI:n läser av vilken pryl som säljs, var den sitter
   * och vad som är skräp runt omkring. Körs en gång per bild (går att göra om).
   */
  const analyzeFn = useServerFn(analyzeStudioImage);
  const runStudioAnalysis = useCallback(
    async (index: number, force = false) => {
      const source = cleanOriginals[index] ?? images[index];
      if (!source) return;
      if (!force && studioInfo[index] !== undefined) return;
      setAnalyzingStudio(index);
      try {
        const info = await analyzeFn({
          data: { imageBase64: source.imageBase64, mimeType: source.mimeType },
        });
        setStudioInfo((prev) => ({ ...prev, [index]: (info as StudioSubject | null) ?? null }));
      } catch {
        setStudioInfo((prev) => ({ ...prev, [index]: null }));
      } finally {
        setAnalyzingStudio(null);
      }
    },
    [analyzeFn, cleanOriginals, images, studioInfo],
  );

  // Läs av bilden så fort studion öppnas – då vet redigeringen vad som ska behållas.
  useEffect(() => {
    if (cleanDialog === null) return;
    void runStudioAnalysis(cleanDialog);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cleanDialog]);

  // När studion öppnas: räkna ut vilken version annonsbilden faktiskt är, så att
  // rätt ruta är markerad även efter omladdning.
  useEffect(() => {
    const index = cleanDialog;
    if (index === null) return;
    setVariantNote(null);
    setStudioSelection((prev) => {
      if (prev[index] !== undefined) return prev;
      const current = images[index];
      if (!current) return prev;
      const hit = Object.entries(cleanResults).find(
        ([k, v]) => k.startsWith(`${index}:`) && v.imageBase64 === current.imageBase64,
      );
      return { ...prev, [index]: hit ? hit[0] : null };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cleanDialog]);

  /** Bilden som studion visar just nu (vald version, annars originalet/annonsbilden). */
  const studioPreview = useCallback(
    (index: number) => {
      const key = studioSelection[index];
      if (key) {
        const hit = cleanResults[key];
        if (hit) return hit;
      }
      return cleanOriginals[index] ?? images[index] ?? null;
    },
    [studioSelection, cleanResults, cleanOriginals, images],
  );

  const cleanBackground = useCallback(
    async (index: number, variant?: number, mode?: CleanMode) => {
      const current = images[index];
      if (!current) return;
      const useMode = mode ?? cleanMode[index] ?? "background";
      setCleanMode((prev) => ({ ...prev, [index]: useMode }));
      setVariantNote(null);

      // Redan gjord tidigare i den här sessionen? Visa den direkt, utan AI.
      const localKey = `${index}:${useMode}:${variant ?? 1}`;
      const saved = cleanResults[localKey];
      if (saved) {
        setCleanOriginals((prev) => ({
          ...prev,
          [index]: prev[index] ?? { imageBase64: current.imageBase64, mimeType: current.mimeType },
        }));
        setCleanVariants((prev) => ({ ...prev, [`${index}:${useMode}`]: variant ?? 1 }));
        setStudioSelection((prev) => ({ ...prev, [index]: localKey }));
        setStudioDirty((prev) => ({ ...prev, [index]: true }));
        setCleanCached(true);
        setError(null);
        return;
      }

      setCleaning(index);
      setError(null);
      setCleanWarning(null);
      try {
        const original = cleanOriginals[index] ?? current;

        // Har AI:n läst av bilden beskär vi till prylen först – då finns skräpet
        // runt omkring inte ens kvar i det bildmodellen får se.
        let info = studioInfo[index];
        if (info === undefined) {
          try {
            info = ((await analyzeFn({
              data: { imageBase64: original.imageBase64, mimeType: original.mimeType },
            })) ?? null) as StudioSubject | null;
            setStudioInfo((prev) => ({ ...prev, [index]: info ?? null }));
          } catch {
            info = null;
          }
        }

        let source = original;
        if (info?.box) {
          const { cropBase64 } = await import("@/lib/image-compress");
          source = await cropBase64(
            original.imageBase64,
            original.mimeType,
            info.box,
            useMode === "angle" ? 0.06 : 0.12,
          );
        }

        const cleaned = await cleanImageFn({
          data: {
            imageBase64: source.imageBase64,
            mimeType: source.mimeType,
            mode: useMode,
            ...(variant ? { variant } : {}),
            ...(info?.subject ? { subject: info.subject } : {}),
            ...(info?.distractors?.length ? { distractors: info.distractors.slice(0, 8) } : {}),
          },
        });

        if (cleaned.error) {
          setCleanQuota(cleaned.quota);
          setError(cleaned.error);
          return;
        }
        setCleanOriginals((prev) => ({
          ...prev,
          [index]: prev[index] ?? { imageBase64: current.imageBase64, mimeType: current.mimeType },
        }));
        setCleanQuota(cleaned.quota);
        setCleanCached(cleaned.cached);
        setCleanWarning(cleaned.warning ?? null);

        setCleanVariants((prev) => ({ ...prev, [`${index}:${useMode}`]: cleaned.variant }));

        // Gemini svarar med en stor PNG – komprimera till JPEG innan den sparas.
        const { compressImage } = await import("@/lib/image-compress");
        const blob = await (
          await fetch(`data:${cleaned.mimeType};base64,${cleaned.imageBase64}`)
        ).blob();
        const compressed = await compressImage(blob);
        const base64 = await new Promise<string>((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => resolve(String(reader.result).split(",")[1] ?? "");
          reader.onerror = () => reject(new Error("Kunde inte läsa bilden."));
          reader.readAsDataURL(compressed);
        });
        const next = base64 ? { imageBase64: base64, mimeType: "image/jpeg" } : cleaned;
        const key = `${index}:${useMode}:${cleaned.variant}`;
        const previousKey = `${index}:${useMode}:${cleaned.variant - 1}`;
        const previous = cleanResults[previousKey];
        if (cleaned.variant > 1) {
          if (previous && previous.imageBase64 === next.imageBase64) {
            setVariantNote(
              `Variant ${cleaned.variant} blev i princip identisk med den förra – behåll variant ${cleaned.variant - 1} eller originalet.`,
            );
          } else if (cleaned.warning) {
            setVariantNote(
              `Variant ${cleaned.variant} blev inte bättre – behåll en tidigare variant eller originalet.`,
            );
          }
        }
        setCleanResults((prev) => ({
          ...prev,
          [key]: { imageBase64: next.imageBase64, mimeType: next.mimeType },
        }));
        // Bara förhandsvisning: annonsbilden ändras först när man väljer "Använd bilden".
        setStudioSelection((prev) => ({ ...prev, [index]: key }));
        setStudioDirty((prev) => ({ ...prev, [index]: true }));
        if (cleaned.debug) setCleanDebug(cleaned.debug);
      } catch (err) {
        setError(err instanceof Error ? err.message : "Kunde inte rensa bakgrunden.");
      } finally {
        setCleaning(null);
      }
    },
    [cleanImageFn, analyzeFn, images, cleanOriginals, cleanMode, cleanResults, studioInfo],
  );

  /** Skriver in studions valda version i annonsen. Enda stället där bilden byts. */
  const applyStudioSelection = useCallback(
    (index: number) => {
      const picked = studioPreview(index);
      if (picked) {
        setImages((prev) =>
          prev.map((img, i) =>
            i === index
              ? { ...img, imageBase64: picked.imageBase64, mimeType: picked.mimeType }
              : img,
          ),
        );
      }
      setStudioDirty((prev) => ({ ...prev, [index]: false }));
      setCleanDialog(null);
    },
    [studioPreview],
  );

  /** Stäng utan att ändra annonsbilden – valet nollställs till nuvarande bild. */
  const discardStudioSelection = useCallback((index: number) => {
    setStudioSelection((prev) => {
      const next = { ...prev };
      delete next[index];
      return next;
    });
    setStudioDirty((prev) => ({ ...prev, [index]: false }));
    setVariantNote(null);
    setCleanDialog(null);
  }, []);

  /**
   * Roterar den version som visas i studion. Rotationen sparas på just den
   * versionen (eller originalet) – annonsbilden rörs inte förrän man godkänner.
   */
  const rotateStudioImage = useCallback(
    async (index: number, degrees: 90 | -90) => {
      const current = studioPreview(index);
      if (!current) return;
      setRotating(index);
      try {
        const { rotateBase64 } = await import("@/lib/image-compress");
        const rotated = await rotateBase64(current.imageBase64, current.mimeType, degrees);
        const key = studioSelection[index];
        if (key && cleanResults[key]) {
          setCleanResults((prev) => ({ ...prev, [key]: rotated }));
        } else if (cleanOriginals[index]) {
          setCleanOriginals((prev) => ({ ...prev, [index]: rotated }));
        } else {
          setImages((prev) => prev.map((img, i) => (i === index ? { ...img, ...rotated } : img)));
        }
        setStudioDirty((prev) => ({ ...prev, [index]: true }));
      } catch {
        setError("Kunde inte rotera bilden.");
      } finally {
        setRotating(null);
      }
    },
    [studioPreview, studioSelection, cleanResults, cleanOriginals],
  );

  /** "Ångra"/"Original": visa originalet igen. Versionerna finns kvar. */
  const undoClean = useCallback((index: number) => {
    setStudioSelection((prev) => ({ ...prev, [index]: null }));
    setStudioDirty((prev) => ({ ...prev, [index]: true }));
    setCleanWarning(null);
    setVariantNote(null);
  }, []);

  const rotateImage = useCallback(
    async (index: number) => {
      setRotating(index);
      try {
        const { rotateBase64 } = await import("@/lib/image-compress");
        const current = images[index];
        if (!current) return;
        const rotated = await rotateBase64(current.imageBase64, current.mimeType, 90);
        setImages((prev) => prev.map((img, i) => (i === index ? { ...img, ...rotated } : img)));
      } catch {
        setError("Kunde inte rotera bilden.");
      } finally {
        setRotating(null);
      }
    },
    [images],
  );

  /** Byter plats på två bilder – första bilden är alltid huvudbild. */
  const imageDrag = useDragReorder(images.length, (from, to) => {
    setImages((prev) => moveItem(prev, from, to));
    remapStudio(moveIndexMap(from, to));
  });

  const moveImage = useCallback(
    (index: number, dir: -1 | 1) => {
      const target = index + dir;
      setImages((prev) => {
        if (target < 0 || target >= prev.length) return prev;
        const next = [...prev];
        [next[index], next[target]] = [next[target], next[index]];
        return next;
      });
      if (target >= 0 && target < images.length) remapStudio(swapIndexMap(index, target));
    },
    [images.length, remapStudio],
  );

  const confirmRemoveImage = useCallback((index: number) => {
    setImageToRemove(index);
  }, []);

  const doRemoveImage = useCallback(() => {
    if (imageToRemove === null) return;
    setImages((prev) => {
      const next = prev.filter((_, i) => i !== imageToRemove);
      if (!next.length) setHasDraft(false);
      return next;
    });
    // Studiodata (original, versioner, läge) följer med bilden bort.
    remapStudio(removeIndexMap(imageToRemove));
    setCleanDialog(null);
    setImageToRemove(null);
  }, [imageToRemove, remapStudio]);

  const reviewRef = useRef<HTMLDivElement>(null);

  const [pendingScroll, setPendingScroll] = useState(false);

  /** Scrollar så att "Granska och justera" hamnar högst upp när AI-texten är klar. */
  const scrollToReview = useCallback(() => setPendingScroll(true), []);

  /**
   * Efter inloggning/registrering ska man tillbaka exakt dit man var i flödet.
   * Finns ingen sparad position faller vi tillbaka på granskningsrutan.
   */
  const restoreAfterAuth = useCallback(() => {
    let saved = 0;
    try {
      saved = Number(window.sessionStorage.getItem(SELL_FLOW_SCROLL_KEY) ?? 0);
    } catch {
      saved = 0;
    }
    if (saved > 0) {
      window.setTimeout(() => window.scrollTo({ top: saved, behavior: "smooth" }), 80);
      return;
    }
    setPendingScroll(true);
  }, []);

  useEffect(() => {
    if (!pendingScroll) return;
    // Förloppsmodalen låser sidans scroll – vänta tills den är stängd.
    if (aiProgress) return;
    const OFFSET = 80;
    let cancelled = false;
    let tries = 0;
    let correction = 0;

    const scrollNow = (el: HTMLElement) => {
      scrollToElement(el, OFFSET);
    };

    const tick = () => {
      if (cancelled) return;
      const el = reviewRef.current;
      if (!el) {
        if (tries++ < 20) requestAnimationFrame(tick);
        else setPendingScroll(false);
        return;
      }
      // Två renderingar så att nytillkomna fält räknas med i höjden.
      requestAnimationFrame(() => {
        if (cancelled) return;
        scrollNow(el);
        setPendingScroll(false);
        // Har innehåll ovanför laddat klart under tiden – justera en gång till.
        correction = window.setTimeout(() => {
          const current = reviewRef.current;
          if (!current) return;
          if (Math.abs(current.getBoundingClientRect().top - OFFSET) > 40) scrollNow(current);
        }, 450);
      });
    };

    requestAnimationFrame(tick);
    return () => {
      cancelled = true;
      window.clearTimeout(correction);
    };
  }, [pendingScroll, aiProgress]);

  const handleGenerate = useCallback(async (focusItem?: string) => {
    if (!images.length && !video) return;
    if (typeof navigator !== "undefined" && !navigator.onLine) {
      setError("Du är offline — materialet är sparat. Försök igen när du är uppkopplad.");
      return;
    }
    setLoading(true);
    setError(null);
    // Alla bilder – plus bildrutor ur videon om det finns en video.
    const payload = images.map((img) => ({ imageBase64: img.imageBase64, mimeType: img.mimeType }));
    const toPart = (dataUrl: string) => {
      const m = dataUrl.match(/^data:([^;]+);base64,(.+)$/);
      return m ? { mimeType: m[1], imageBase64: m[2] } : null;
    };

    let videoFrames = 0;
    if (video) {
      // Alltid tre bildrutor (början, mitten, slutet) så AI:n ser hela filmen –
      // inte bara omslagsbilden. Annars missas saker som dyker upp senare.
      const { extractVideoFrames } = await import("@/lib/video-poster");
      const { adVideoSrc } = await import("@/lib/video-src");
      let urls: string[] = [];
      try {
        urls = await extractVideoFrames(adVideoSrc(video.path), [0.1, 0.5, 0.85]);
      } catch {
        urls = [];
      }
      if (!urls.length && video.poster) urls = [video.poster];
      for (const u of urls) {
        const part = toPart(u);
        if (part && payload.length < 7) {
          payload.push(part);
          videoFrames += 1;
        }
      }
      if (!images.length) {
        // Annonsen behöver ett omslag – använd första bildrutan som annonsbild.
        const cover = toPart(urls[0] ?? "");
        if (cover) setImages([cover]);
      }
    }

    if (!payload.length) {
      setLoading(false);
      setError("Vi kunde inte läsa av din film. Lägg till en bild och försök igen.");
      return;
    }

    const photoCount = payload.length - videoFrames;
    const steps = [
      ...payload.map((_, i) =>
        i >= photoCount
          ? videoFrames > 1
            ? `Granskar bildruta ${i - photoCount + 1} av ${videoFrames} ur din film`
            : "Granskar bildrutan ur din film"
          : `Granskar bild ${i + 1} av ${photoCount}`,
      ),
      "Skriver rubrik och beskrivning",
      "Analyserar pris och kategori",
      "Grupperar prylar",
    ];
    // Miniatyr per bildsteg så man ser vilken bild som granskas just nu.
    const thumbs: (string | null)[] = [
      ...payload.map((p) => `data:${p.mimeType};base64,${p.imageBase64}`),
      null,
      null,
      null,
    ];
    setAiProgress({ steps, thumbs, done: false });
    try {
      const ad = await generateAdFn({
        data: {
          images: payload,
          videoFrame: videoFrames > 0,
          videoFrames,
          ...(focusItem ? { focusItem } : {}),
        },
      });
      // Tomt svar (t.ex. avbruten uppkoppling) får aldrig krascha flödet.
      if (!ad || typeof ad !== "object") {
        throw new Error(
          "Vi fick inget svar från AI:n den här gången. Försök igen om en liten stund.",
        );
      }
      setAdminTrace((ad as { adminTrace?: AdminTraceStep[] | null }).adminTrace ?? null);

      const picked = ad as {
        comment?: string;
        sellPick?: { items?: string[]; reason?: string; alternatives?: string[]; tip?: string };
        needsPersonRemoval?: boolean;
      };
      setAiInsight(
        picked.comment || picked.sellPick
          ? {
              comment: picked.comment,
              items: picked.sellPick?.items,
              reason: picked.sellPick?.reason,
              alternatives: picked.sellPick?.alternatives,
              tip: picked.sellPick?.tip,
              needsPersonRemoval: picked.needsPersonRemoval,
            }
          : null,
      );

      const photo = (ad as { photoQuality?: PhotoQuality }).photoQuality ?? null;
      const usable = !photo || photo.verdict !== "unusable";
      setPhotoQuality(photo && photo.verdict !== "ok" ? photo : null);
      const finding = (ad as { contactFinding?: ContactFinding | null }).contactFinding ?? null;
      setContactFinding(
        finding?.confidence === "high" &&
          finding.imageIndex >= 0 &&
          finding.imageIndex < images.length
          ? finding
          : null,
      );

      const applyAd = () => {
        setForm((f) => ({
          ...f,
          title: ad.title,
          description: ad.description,
          price: String(ad.price),
          category: CATEGORIES.some((c) => c.slug === ad.category)
            ? (ad.category as string)
            : "ovrigt",
          contactEmail: f.contactEmail || user?.email || "",
        }));
        // Kommer man från Mina prylar har vi AI:ns värdering som prisförslag.
        try {
          const raw = sessionStorage.getItem("sellavo.scan-prefill");
          if (raw) {
            sessionStorage.removeItem("sellavo.scan-prefill");
            const prefill = JSON.parse(raw) as {
              price?: number;
              name?: string;
              title?: string;
              description?: string;
              fromDeepValuation?: boolean;
            };
            if (prefill?.fromDeepValuation) {
              setPrefillNotice(VALUATION_DISCLAIMER);
            }
            if (prefill?.price && prefill.price > 0) {
              setForm((f) => ({ ...f, price: String(Math.round(prefill.price!)) }));
              toast.info("Prisförslaget kommer från din djupvärdering (AI-uppskattning).", {
                description: prefill.name ? `Pryl: ${prefill.name}` : undefined,
              });
            }
            // Kommer man från en djupvärdering har vi redan en färdig annonstext.
            if (prefill?.title || prefill?.description) {
              setForm((f) => ({
                ...f,
                title: prefill.title?.slice(0, 70) || f.title,
                description: prefill.description || f.description,
              }));
              toast.info("Rubrik och text kommer från din djupvärdering (AI-uppskattning).", {
                description: "Läs igenom och ändra det som inte stämmer.",
              });
            }
          }
        } catch {
          // Prefill är en bonus – strunta i fel.
        }
        setAiTags(
          Array.isArray((ad as { tags?: string[] }).tags) ? (ad as { tags?: string[] }).tags! : [],
        );
        if ((ad as { people?: boolean }).people) setPeopleInImages(true);
        setHasDraft(true);
        if (!user) {
          track("ai_locked");
        }
        // Grupper: bara bilder (videons bildruta hör till den grupp den hamnat i men kan inte flyttas).
        const rawGroups = ((ad as { groups?: SplitGroup[] }).groups ?? []) as SplitGroup[];
        const groups = rawGroups
          .map((g) => ({
            ...g,
            imageIndexes: (g.imageIndexes ?? []).filter((i: number) => i < images.length),
          }))
          .filter((g) => g.imageIndexes.length > 0);
        const cohesion = ((ad as { cohesion?: "same" | "related" | "mixed" }).cohesion ??
          "same") as "same" | "related" | "mixed";
        setSplitInfo(groups.length > 1 ? { cohesion, groups } : null);

        const notes = Array.isArray((ad as { imageNotes?: string[] }).imageNotes)
          ? (ad as { imageNotes?: string[] }).imageNotes!
          : [];
        const groupNote =
          groups.length > 1
            ? `${groups.length} prylar: ${groups
                .map((g) => g.label)
                .filter(Boolean)
                .join(", ")}`
            : "En pryl";
        const allNotes = [...notes];
        allNotes[steps.length - 1] = groupNote;
        setAiProgress({ steps, thumbs, notes: allNotes, done: true });
        window.setTimeout(() => setAiProgress(null), 1600);
        // Förslag på säljsätt (bara när auktioner är igång) och på bud.
        const advice = ad as {
          saleKind?: "fixed" | "auction";
          saleKindReason?: string;
          acceptsOffers?: boolean;
          acceptsOffersReason?: string;
        };
        if (auctionsOn && (advice.saleKind === "fixed" || advice.saleKind === "auction")) {
          setSaleAdvice({ kind: advice.saleKind, reason: advice.saleKindReason?.trim() || "" });
          setSaleKind(advice.saleKind);
        }
        if (typeof advice.acceptsOffers === "boolean") {
          setAcceptsOffers(advice.acceptsOffers);
          setOffersAdvice(advice.acceptsOffersReason?.trim() || "");
        }
        // Kan prylen vara värd betydligt mer erbjuder vi en djupvärdering direkt.
        const hv = ad as {
          highValue?: boolean;
          highValueReason?: string;
          priceLow?: number;
          priceHigh?: number;
          identifiedAs?: string;
          preliminary?: boolean;
        };
        const vLow =
          hv.priceLow && hv.priceLow > 0
            ? Math.round(hv.priceLow)
            : Math.round(Number(ad.price)) || 0;
        const vHigh = hv.priceHigh && hv.priceHigh > 0 ? Math.round(hv.priceHigh) : null;
        setAiValuation(vLow > 0 ? { low: vLow, high: vHigh } : null);
        // Extrakollen har redan skrivit rubrik och text – säg att den är preliminär.
        setPreliminaryNote(
          hv.preliminary
            ? hv.identifiedAs
              ? testFeatures
                ? `Vi tror att det här är: ${hv.identifiedAs}. Rubrik, text och pris är preliminära – en djupvärdering kan skärpa dem. Du kan publicera ändå.`
                : `Vi tror att det här är: ${hv.identifiedAs}. Rubrik, text och pris är preliminära – ändra det som inte stämmer och publicera ändå.`
              : testFeatures
                ? "Rubrik, text och pris är preliminära – en djupvärdering kan skärpa dem. Du kan publicera ändå."
                : "Rubrik, text och pris är preliminära – ändra det som inte stämmer och publicera ändå."
            : null,
        );
        if (hv.highValue && testFeatures) {
          window.setTimeout(
            () =>
              setHighValue({
                reason:
                  hv.highValueReason ||
                  (hv.identifiedAs
                    ? `Vi tror att det här är: ${hv.identifiedAs}. Värdet kan variera mycket.`
                    : "Det här ser ut att vara ett föremål där värdet kan variera mycket."),
                low: hv.priceLow,
                high: hv.priceHigh,
              }),
            1700,
          );
        }

        scrollToReview();
      };

      if (usable) {
        applyAd();
      } else {
        // Bilden duger inte – vi fyller inte i något förrän säljaren väljer att köra vidare.
        pendingAdRef.current = applyAd;
        setAiProgress(null);
      }
    } catch (err) {
      setAiProgress(null);
      const message = err instanceof Error ? err.message : "Något gick fel";
      setError(message);
      // Gratisgränsen utan konto – visa registreringen direkt.
      if (!user && /konto/i.test(message)) {
        setAuthMode("register");
        setAuthOpen(true);
      }
    } finally {
      setLoading(false);
    }
  }, [images, video, generateAdFn, user, scrollToReview]);

  // Kommer man från Mina prylar tar vi med prylens bilder rakt in i flödet
  // och startar granskningen automatiskt – utan att visa upload-steget.
  const prefillStarted = useRef(false);
  const [prefillLoading, setPrefillLoading] = useState(false);
  const [prefillConflict, setPrefillConflict] = useState<string | null>(null);

  const runPrefill = useCallback(async (raw: string) => {
    let prefill: { fromDeepValuation?: boolean; imageUrls?: string[] } | null = null;
    try {
      prefill = JSON.parse(raw) as { fromDeepValuation?: boolean; imageUrls?: string[] };
    } catch {
      return;
    }
    // Ett förifyllningskommando får bara användas en gång. Om användaren
    // lämnar flödet och kommer tillbaka ska det sparade utkastet visas i
    // befintligt skick – ingen ny scanning eller annonsgenerering får starta.
    try {
      sessionStorage.removeItem("sellavo.scan-prefill");
    } catch {
      // Fortsätt även om sessionslagring är blockerad.
    }
    if (prefill?.fromDeepValuation) {
      setPrefillNotice(VALUATION_DISCLAIMER);
    }
    const urls = prefill?.imageUrls ?? [];
    if (!urls.length) return;
    setPrefillLoading(true);
    // Hoppa direkt ner till formuläret så man inte landar i hero-vyn.
    window.setTimeout(
      () =>
        document
          .getElementById("skapa-form")
          ?.scrollIntoView({ behavior: "smooth", block: "start" }),
      80,
    );
    try {
      const loaded: AdImage[] = [];
      for (const url of urls.slice(0, maxImages)) {
        try {
          const res = await fetch(url);
          if (!res.ok) continue;
          const blob = await res.blob();
          const base64 = await new Promise<string>((resolve, reject) => {
            const reader = new FileReader();
            reader.onloadend = () => resolve((reader.result as string).split(",")[1]);
            reader.onerror = () => reject(new Error("read"));
            reader.readAsDataURL(blob);
          });
          loaded.push({ imageBase64: base64, mimeType: blob.type || "image/jpeg" });
        } catch {
          // Hoppar över bilder som inte går att hämta.
        }
      }
      if (!loaded.length) {
        toast.error("Vi kunde inte hämta bilden från din pryl. Ladda upp en bild här istället.");
        return;
      }
      setImages(loaded);
      clearStudio();
    } finally {
      setPrefillLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Ett förifyllningskommando kan dyka upp både på startsidan och på /skapa.
  // Har man redan ett utkast frågar vi först – annars går vi direkt till /skapa.
  useEffect(() => {
    if (!restored || prefillStarted.current) return;
    let raw: string | null = null;
    try {
      raw = sessionStorage.getItem("sellavo.scan-prefill");
    } catch {
      return;
    }
    if (!raw) return;
    prefillStarted.current = true;
    // Har man redan ett påbörjat utkast frågar vi först vad som ska hända.
    if (images.length) {
      setPrefillConflict(raw);
      return;
    }
    if (variant !== "create") {
      void navigate({ to: "/skapa", replace: true });
      return;
    }
    void runPrefill(raw);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [restored, variant]);

  /** Behåll det gamla utkastet – prylen får skapas senare. */
  const keepDraftOverPrefill = useCallback(async () => {
    try {
      sessionStorage.removeItem("sellavo.scan-prefill");
    } catch {
      // Struntar i fel.
    }
    // Står vi på startsidan: gå först till flödet, stäng modalen först när vi
    // är framme – annars blinkar startsidan förbi bakom dialogen.
    if (variant !== "create") await navigate({ to: "/skapa", replace: true });
    setPrefillConflict(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [variant]);

  /** Släng utkastet och fortsätt med prylen från Mina prylar. */
  const startOverWithPrefill = useCallback(async () => {
    const raw = prefillConflict;
    await clearDraft();
    setImages([]);
    clearStudio();

    setVideo(null);
    setPendingGroups([]);
    setForm(emptyForm);
    setSaleKind("fixed");
    setReservePrice("");
    setAuctionDays(7);
    setPeopleInImages(false);
    setPeopleConsent(false);
    setAccepted(false);
    setSaleAdvice(null);
    setAiValuation(null);
    setPriceMotivation("");
    setAiTags([]);
    setSplitInfo(null);
    setPublished(null);
    setHasDraft(false);
    setDraftSavedAt(null);
    if (variant !== "create") {
      // Kommandot ligger kvar i sessionStorage och plockas upp på /skapa.
      await navigate({ to: "/skapa", replace: true });
      setPrefillConflict(null);
      return;
    }
    setPrefillConflict(null);
    if (raw) void runPrefill(raw);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prefillConflict, runPrefill, variant]);

  /** Behåll allt i en annons – slå ihop gruppernas texter och priser. */
  const handleKeepAsOne = useCallback(() => {
    const groups = splitInfo?.groups ?? [];
    if (groups.length > 1) {
      const title = groups
        .map((g) => g.label || g.title)
        .filter(Boolean)
        .join(" + ")
        .slice(0, 120);
      const description = groups.map((g) => `${g.label}: ${g.description}`).join("\n\n");
      const price = groups.reduce((sum, g) => sum + (Number(g.price) || 0), 0);
      setForm((f) => ({
        ...f,
        title: title || f.title,
        description,
        price: price > 0 ? String(price) : f.price,
      }));
      setAiTags((prev) =>
        Array.from(new Set([...prev, ...groups.flatMap((g) => g.tags ?? [])])).slice(0, 12),
      );
    }
    setSplitInfo(null);
  }, [splitInfo]);

  /** Dela upp: första gruppen blir den här annonsen, resten köas som utkast. */
  const handleSplit = useCallback(() => {
    const groups = splitInfo?.groups ?? [];
    if (groups.length < 2) return;
    const [first, ...rest] = groups;
    setImages(first.imageIndexes.map((i) => images[i]).filter(Boolean));
    clearStudio();

    setForm((f) => ({
      ...f,
      title: first.title,
      description: first.description,
      price: String(first.price),
      category: CATEGORIES.some((c) => c.slug === first.category)
        ? (first.category as string)
        : f.category,
    }));
    setAiTags(first.tags ?? []);
    setPendingGroups(
      rest
        .map((g) => ({
          label: g.label,
          title: g.title,
          description: g.description,
          price: String(g.price),
          category: CATEGORIES.some((c) => c.slug === g.category)
            ? (g.category as string)
            : "ovrigt",
          tags: g.tags ?? [],
          images: g.imageIndexes.map((i) => images[i]).filter(Boolean),
        }))
        .filter((g) => g.images.length > 0),
    );
    setSplitInfo(null);
    toast.success("Annonsen delades upp", {
      description: "Du lägger ut en pryl i taget – nästa startar direkt när den här är publicerad.",
    });
    scrollToReview();
  }, [splitInfo, images, scrollToReview]);

  /** Starta nästa köade annons efter publicering. */
  const handleNextPending = useCallback(() => {
    setPendingGroups((prev) => {
      const [next, ...rest] = prev;
      if (!next) return prev;
      setImages(next.images);
      clearStudio();

      setVideo(null);
      setForm((f) => ({
        ...f,
        title: next.title,
        description: next.description,
        price: next.price,
        category: next.category,
      }));
      setAiTags(next.tags);
      setPublished(null);
      setAccepted(false);
      setHasDraft(true);
      scrollToReview();
      return rest;
    });
  }, [scrollToReview]);

  /** Hoppar till fältet som behöver fixas. */
  const goToIssue = useCallback((issue: PublishIssue) => {
    setPublishFlow(null);
    if (issue.target === "review") {
      setPendingScroll(true);
      return;
    }
    if (!issue.target) return;
    window.setTimeout(() => {
      const el = document.getElementById(issue.target!);
      if (!el) return;
      scrollToElement(el, 100);
      if (el instanceof HTMLInputElement || el instanceof HTMLTextAreaElement) el.focus();
    }, 120);
  }, []);

  const fieldError = useCallback(
    (id: "title" | "description" | "price" | "category" | "location"): string | undefined => {
      if (!attempted) return undefined;
      // Samma regelverk som servern kör – ingen egen kopia här.
      const msg = adFieldError(id, {
        title: form.title,
        description: form.description,
        price: form.price,
        saleKind,
        category: form.category,
        location: form.location,
        country: adCountry,
      });
      if (id === "location" && msg) return "Välj en ort i listan – eller använd en sparad adress.";
      return msg;
    },
    [attempted, form.title, form.description, form.price, saleKind, form.category, form.location, adCountry],
  );

  /** Sparar ett ifyllt visningsnamn automatiskt när man trycker publicera. */
  const displayNameSaveRef = useRef<(() => Promise<string | null>) | null>(null);
  const registerDisplayNameSave = useCallback((fn: (() => Promise<string | null>) | null) => {
    displayNameSaveRef.current = fn;
  }, []);
  /** Vad som just nu står i namnrutan (även om det inte sparats än). */
  const [displayNameDraft, setDisplayNameDraft] = useState("");
  const handleDisplayNameDraft = useCallback((v: string) => setDisplayNameDraft(v), []);

  const runPublish = useCallback(
    async (
      override?: { title: string; description: string },
      opts?: {
        skipPriceWarning?: boolean;
        skipQuality?: boolean;
        skipImageWarning?: boolean;
        priceApprovalMotivation?: string;
      },
    ) => {
      const title = override?.title ?? form.title;
      const description = override?.description ?? form.description;
      if (!images.length || !user) return;

      // Har man skrivit in ett visningsnamn men inte tryckt "Spara namn" – spara det åt användaren.
      let contactName = form.contactName;
      if (!contactName && displayNameSaveRef.current) {
        contactName = (await displayNameSaveRef.current()) ?? "";
      }

      const steps = [
        { label: flowText.checking, status: "active" as const },
        { label: flowText.reviewing, status: "pending" as const },
        { label: flowText.reviewingPrice, status: "pending" as const },
        {
          label: images.length
            ? video
              ? flowText.uploading
              : flowText.uploading
            : video
              ? flowText.uploading
              : flowText.translating,
          status: "pending" as const,
        },
      ];
      setPublishFlow({ phase: "running", steps });
      setError(null);

      // Steg 1 – vad saknas?
      const issues: PublishIssue[] = [];
      if (!contactName)
        issues.push(
          displayNameDraft.trim()
            ? {
                label: "Namnet är upptaget – välj ett annat visningsnamn och tryck Spara namn.",
                target: "f-profil",
              }
            : {
                label: "Visningsnamn saknas – fyll i din profil först.",
                to: "/profil",
                toLabel: "Till profilen",
              },
        );

      if (!form.title.trim()) issues.push({ label: flowText.titleMissing, target: "f-title" });
      if (!form.description.trim())
        issues.push({ label: flowText.descriptionMissing, target: "f-desc" });
      if (!form.price || Number(form.price) <= 0)
        issues.push({ label: flowText.priceMissing, target: "f-price" });
      if (!form.category) issues.push({ label: flowText.categoryMissing, target: "f-cat" });
      if (!isValidLocalityForCountry(form.location, adCountry))
        issues.push({
          label:
            flowText.locationHelp,
          target: "f-loc",
        });
      if (peopleInImages && !peopleConsent)
        issues.push({
          label: flowText.consentHelp,
          target: "review",
        });
      if (!accepted) issues.push({ label: flowText.termsMissing, target: "f-villkor" });
      if (quotaBlocked)
        issues.push({
          label: planStatus?.quota.reason ?? flowText.quota,
          to: "/priser",
          toLabel: flowText.plans,
        });

      if (issues.length) {
        setPublishFlow({
          phase: "blocked",
          steps: [
            {
              ...steps[0],
              status: "error",
              note: `${issues.length} sak${issues.length === 1 ? "" : "er"} behöver fixas`,
            },
            steps[1],
            steps[2],
            steps[3],
          ],
          issues,
        });
        return;
      }

      setPublishing(true);
      try {
        setPublishFlow({
          phase: "running",
          steps: [
            { ...steps[0], status: "done", note: "Allt ifyllt" },
            { ...steps[1], status: "active" },
            steps[2],
            steps[3],
          ],
        });
        if (!override) {
          const check = await checkAdText({ data: { title, description } });
          if (!check.ok) {
            setModeration({ check, title, description });
            setPublishFlow({
              phase: "blocked",
              steps: [
                { ...steps[0], status: "done" },
                { ...steps[1], status: "error", note: "Texten behöver justeras" },
                steps[2],
                steps[3],
              ],
              issues: [
                {
                  label:
                    "Vi hittade kontaktuppgifter eller olämpligt innehåll i texten. Granska förslaget i formuläret.",
                  target: "review",
                },
              ],
            });
            setPublishing(false);
            return;
          }
        }
        setModeration(null);

        // Kvalitetsgranskning – teckensallad, texter som blandar flera olika prylar
        // och tips om bilderna. Blockerar bara när något är uppenbart fel.
        if (!opts?.skipQuality) {
          let quality: QualityCheck = QUALITY_OK;
          try {
            // Har annonsen en film tar vi ut några bildrutor så att granskningen
            // kan se att filmen faktiskt visar samma pryl som texten.
            let videoFrames: { imageBase64: string; mimeType: string }[] = [];
            if (video?.path) {
              try {
                const { extractVideoFrames } = await import("@/lib/video-poster");
                const frames = await extractVideoFrames(adVideoSrc(video.path), [0.1, 0.5, 0.85]);
                videoFrames = frames
                  .map((f) => ({
                    imageBase64: f.split(",")[1] ?? "",
                    mimeType: "image/jpeg",
                  }))
                  .filter((f) => f.imageBase64)
                  .slice(0, 3);
              } catch {
                videoFrames = [];
              }
            }
            quality = await checkAdQuality({
              data: {
                title,
                description,
                images: images.slice(0, 3).map((img) => ({
                  imageBase64: img.imageBase64,
                  mimeType: img.mimeType,
                })),
                videoFrames,
              },
            });
          } catch {
            quality = QUALITY_OK;
          }

          if (quality.verdict === "block") {
            setQualityRewrite(quality.rewrite ?? null);
            setPublishFlow({
              phase: "blocked",
              steps: [
                { ...steps[0], status: "done" },
                { ...steps[1], status: "error", note: "Texten behöver justeras" },
                steps[2],
                steps[3],
              ],
              quality: {
                reasons: quality.reasons,
                canRewrite: Boolean(quality.rewrite),
                target: quality.field === "title" ? "f-title" : "f-desc",
                items: quality.bundleKind === "mixed" ? quality.items : [],
                mixedReason: quality.mixedReason ?? null,
              },
              imageWarnings: quality.imageWarnings,
            });
            setPublishing(false);
            return;
          }
          if (quality.imageWarnings.length && !opts?.skipImageWarning) {
            setPublishFlow({
              phase: "warning",
              steps: [],
              imageWarnings: quality.imageWarnings,
            });
            setPublishing(false);
            return;
          }
        }

        // Prisgranskning – AI:n varnar för lockpriser och orimligt höga priser.
        setPublishFlow({
          phase: "running",
          steps: [
            { ...steps[0], status: "done", note: "Allt ifyllt" },
            { ...steps[1], status: "done", note: "Texten är godkänd" },
            { ...steps[2], status: "active" },
            steps[3],
          ],
        });
        let assessment = priceAssessmentRef.current;
        if (!opts?.skipPriceWarning) {
          try {
            assessment = await checkAdPriceFn({
              data: {
                title,
                description,
                category: form.category,
                price: Number(form.price),
              },
            });
          } catch {
            assessment = { flag: null, note: "" };
          }
          priceAssessmentRef.current = assessment;
          if (assessment?.flag) {
            setPublishFlow({
              phase: "warning",
              steps: [],
              priceWarning: {
                title: priceFlagTitle(assessment.flag),
                note: assessment.note || priceFlagShort(assessment.flag),
                range: formatPriceRange(assessment.min, assessment.max),
              },
            });
            setPublishing(false);
            return;
          }
        }

        setPublishFlow({
          phase: "running",
          steps: [
            { ...steps[0], status: "done", note: "Allt ifyllt" },
            { ...steps[1], status: "done", note: "Texten är godkänd" },
            {
              ...steps[2],
              status: assessment?.flag ? "error" : "done",
              note: assessment?.flag ? "Priset flaggas för kontroll" : "Priset ser rimligt ut",
            },
            {
              ...steps[3],
              status: "active",
              note: `${images.length} ${images.length === 1 ? "bild" : "bilder"}${video ? " och en video" : ""}`,
            },
          ],
        });
        const publishedAd = await publishAdFn({
          data: {
            images: images.map((img) => ({ imageBase64: img.imageBase64, mimeType: img.mimeType })),
            videoPath: video?.path ?? "",
            title,
            description,
            price: Number(form.price),
            category: form.category,
            location: form.location,
            address: form.address,
            contactName,
            contactEmail: form.contactEmail,
            contactPhone: form.contactPhone,
            tags: aiTags,
            peopleConsent,
            priceFlag: assessment?.flag ?? null,
            priceNote: assessment?.note ?? "",
            priceMin: assessment?.min ?? null,
            priceMax: assessment?.max ?? null,
            priceApprovalMotivation: opts?.priceApprovalMotivation,
            publishKey: getDraftId(),
            acceptsOffers,
            hidden: hiddenAd,
            saleKind,
            reservePrice:
              saleKind === "auction"
                ? Math.max(Math.round(Number(reservePrice)) || 0, auctionReserveFloor(form.price))
                : null,
            auctionDays,
            contentLanguage: lang,
            country: adCountry,
          },
        });
        setAdminTrace(publishedAd.adminTrace ?? null);
        if (publishedAd.needsPriceApproval) {
          // Priset är så högt att vi vill titta på annonsen först.
          setPriceApproval({
            min: publishedAd.priceMin ?? null,
            max: publishedAd.priceMax ?? null,
          });
          setPublishFlow(null);
          return;
        }
        if (publishedAd.needsPeopleConsent) {
          setPeopleInImages(true);
          setPublishFlow({
            phase: "blocked",
            steps: [
              { ...steps[0], status: "done" },
              { ...steps[1], status: "done" },
              { ...steps[2], status: "done" },
              { ...steps[3], status: "error", note: "Person på bilden" },
            ],
            issues: [
              {
                label:
                  "Det ser ut att synas en person på någon av bilderna. Intyga att du har samtycke, eller byt bild.",
                target: "review",
              },
            ],
          });
          return;
        }
        setPublished(publishedAd);
        if (publishedAd.adminTrace) setAdminTrace(publishedAd.adminTrace);
        track("publish_done", { adSlug: publishedAd.slug });
        // I appen: liten vibration som kvitto på att annonsen är ute.
        void import("@/app-bridge/core/wrapper").then((m) => m.hapticSignal("success"));

        const usedAfter = planStatus
          ? { ...planStatus.usage, month: planStatus.usage.month + 1 }
          : null;
        const remaining = usedAfter ? remainingThisMonth(activePlan, usedAfter) : null;
        setPublishFlow({
          phase: "success",
          steps: [],
          success: {
            slug: publishedAd.slug,
            title: form.title,
            price: Number(form.price) || null,
            remaining,
            planName: PLANS[activePlan].name,
            canPromote: Boolean(promotionStatus && promotionStatus.remaining > 0),
            promoteDays: promotionStatus?.days ?? 0,
            promoteRemaining: promotionStatus?.remaining ?? 0,
            morePending: pendingGroups.length,
          },
        });
        if (!pendingGroups.length) {
          try {
            void clearDraft();
          } catch {
            /* noop */
          }
        }
      } catch (err) {
        const message = err instanceof Error ? err.message : "Något gick fel";
        setError(message);
        setPublishFlow({ phase: "error", steps: [], message });
      } finally {
        setPublishing(false);
      }
    },
    [
      images,
      video,
      form,
      aiTags,
      publishAdFn,
      user,
      accepted,
      peopleConsent,
      pendingGroups,
      peopleInImages,
      quotaBlocked,
      planStatus,
      activePlan,
      promotionStatus,
      checkAdPriceFn,
      displayNameDraft,
      acceptsOffers,
      hiddenAd,
      saleKind,
      sellCountry,
    ],
  );

  const handlePromoteNow = useCallback(
    async (days: number) => {
      if (!published) return;
      setPromoting(true);
      try {
        const res = await promoteAdFn({ data: { slug: published.slug, days } });
        toast[res.ok ? "success" : "error"](
          res.ok
            ? `Annonsen är framlyft i ${days} ${days === 1 ? "dygn" : "dygn"}.`
            : (res.message ?? "Kunde inte lyfta annonsen."),
        );
        void refetchPromotions();
        if (res.ok)
          setPublishFlow((f) =>
            f?.success ? { ...f, success: { ...f.success, canPromote: false } } : f,
          );
      } catch (err) {
        toast.error(err instanceof Error ? err.message : "Kunde inte lyfta annonsen.");
      } finally {
        setPromoting(false);
      }
    },
    [published, promoteAdFn, refetchPromotions],
  );

  /** Varning om reservationspris – samma regel som används överallt. */
  const valueWarning = useMemo(
    () =>
      auctionValueWarning({
        saleKind,
        price: form.price,
        low: aiValuation?.low ?? null,
        high: aiValuation?.high ?? null,
      }),
    [saleKind, form.price, aiValuation],
  );
  const reserveWarning = useMemo(
    () => reservePriceWarning({ saleKind, price: form.price, reservePrice }),
    [saleKind, form.price, reservePrice],
  );
  /** Förslag som användaren kan ta med ett klick i stället för att gissa. */
  const reserveRecommendation = useMemo(
    () =>
      recommendedReservePrice({
        saleKind,
        price: form.price,
        low: aiValuation?.low ?? null,
        high: aiValuation?.high ?? null,
      }),
    [saleKind, form.price, aiValuation],
  );
  const [reserveAccepted, setReserveAccepted] = useState<number | null>(null);
  const [reserveConfirm, setReserveConfirm] = useState(false);

  const handlePublish = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();
      track("publish_submit");
      if (reserveWarning?.kind === "missing") {
        setReserveConfirm(true);
        return;
      }
      void runPublish();
    },
    [runPublish, reserveWarning],
  );

  const handleReset = useCallback(() => {
    setImages([]);
    clearStudio();
    setVideo(null);
    setHasDraft(false);
    setPublished(null);
    setError(null);
    setAccepted(false);
    setPeopleInImages(false);
    setPeopleConsent(false);
    setSplitInfo(null);
    setPendingGroups([]);
    setPrefillNotice(null);
    setForm(emptyForm);
    setSaleKind("fixed");
    setReservePrice("");
    setAuctionDays(7);
    try {
      void clearDraft();
    } catch {
      /* noop */
    }
    if (fileInputRef.current) fileInputRef.current.value = "";
  }, [clearStudio]);

  const [confirmReset, setConfirmReset] = useState(false);
  const [dropActive, setDropActive] = useState(false);

  const openAuth = (mode: "login" | "register") => {
    setAuthMode(mode);
    setAuthOpen(true);
  };

  // Dra och släpp bilder eller video var som helst i uppladdningsrutan.
  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDropActive(false);
    const files = Array.from(e.dataTransfer.files ?? []);
    if (!files.length) return;
    const videoFile = files.find((f) => f.type.startsWith("video/"));
    const imageFiles = files.filter(
      (f) => f.type.startsWith("image/") || /\.(heic|heif)$/i.test(f.name),
    );
    if (imageFiles.length) void processFiles(imageFiles);
    if (videoFile && canVideo && !videoUploading && !video) void processVideoFile(videoFile);
  };
  const dropProps = {
    onDragOver: (e: React.DragEvent) => {
      if (!e.dataTransfer.types.includes("Files")) return;
      e.preventDefault();
      setDropActive(true);
    },
    onDragLeave: (e: React.DragEvent) => {
      if (e.currentTarget.contains(e.relatedTarget as Node | null)) return;
      setDropActive(false);
    },
    onDrop: handleDrop,
  };

  return (
    <main>
      {/* Hero */}
      <section
        id="skapa"
        className={cn(
          "relative overflow-x-clip bg-ocean-900 px-4 pb-8 pt-6 text-white sm:px-6 sm:pb-20 sm:pt-16 lg:px-8 lg:pt-20",
          // På mobil ska skapa-vyn fylla skärmen så det inte blir en vit lucka under,
          // men innehåll som är högre än skärmen måste få växa så sidan går att scrolla.
          variant === "create" &&
            "flex min-h-[calc(100dvh-8.5rem)] flex-col justify-center sm:block sm:min-h-0",
        )}
      >
        <div className="absolute inset-0 opacity-20">
          <div className="absolute -left-20 -top-20 h-72 w-72 rounded-full bg-ocean-500 blur-3xl" />
          <div className="absolute bottom-0 right-0 h-96 w-96 rounded-full bg-ocean-300 blur-3xl" />
        </div>
        <div className="relative mx-auto max-w-4xl text-center">
          <h1 className="font-heading text-[1.6rem] font-extrabold leading-[1.15] tracking-tight sm:text-5xl lg:text-6xl">
            {variant === "create" ? (
              <>
                {lc.createA} <span className="text-ocean-300">{lc.createB}</span>
              </>
            ) : (
              <>
                {lc.sellA} <span className="text-ocean-300">{lc.sellB}</span>
              </>
            )}
          </h1>
          <p className="mx-auto mt-2 max-w-2xl text-sm leading-relaxed text-ocean-100 sm:mt-6 sm:text-xl">
            {lc.hero}
          </p>
          {variant === "create" && hasDraft && (
            <Button
              type="button"
              variant="ghost"
              className="mt-3 text-ocean-100 hover:bg-white/10 hover:text-white"
              onClick={() => setConfirmReset(true)}
            >
              <RefreshCw className="mr-2 h-4 w-4" /> {lc.reset}
            </Button>
          )}

          {variant === "home" ? (
            <>
              <HeroStart
                asLabel={isMobile && !showModeChooser}
                pressed={heroPressed}
                setPressed={setHeroPressed}
                onOpenPicker={() =>
                  showModeChooser ? setModeSheetOpen(true) : setStartDialogOpen(true)
                }
                onFileChange={handleHomeFileChange}
              />
              <Sheet open={modeSheetOpen} onOpenChange={setModeSheetOpen}>
                <SheetContent side="bottom" className="rounded-t-2xl">
                  <SheetHeader className="text-left">
                    <SheetTitle className="font-heading">{lc.what}</SheetTitle>
                    <SheetDescription>{lc.chooseStart}</SheetDescription>
                  </SheetHeader>
                  <Suspense fallback={null}>
                    <ScanModePicker
                      className="mt-4"
                      isLocked={(mode) =>
                        mode.requiresPlan ? !planAtLeast(activePlan, mode.requiresPlan) : false
                      }
                      onSelect={(mode, locked) => {
                        setModeSheetOpen(false);
                        if (mode.id === "single") {
                          if (isMobile) homeInputRef.current?.click();
                          else setStartDialogOpen(true);
                          return;
                        }
                        if (mode.needsAuth && !user) {
                          setAuthOpen(true);
                          return;
                        }
                        if (locked) {
                          void navigate({ to: "/priser" });
                          return;
                        }
                        const lage =
                          mode.id === "photos" ? "foton" : mode.id === "deep" ? "djup" : "film";
                        void navigate({ to: "/mina-prylar", search: { lage, start: 1 } });
                      }}
                    />
                  </Suspense>
                </SheetContent>
              </Sheet>
              <input
                ref={homeInputRef}
                type="file"
                accept={IMAGE_ACCEPT}
                multiple
                className="hidden"
                onChange={(e) => void handleHomeFileChange(e.currentTarget)}
              />
              {startDialogOpen ? (
                <Suspense fallback={null}>
                  <StartCaptureDialog
                    open={startDialogOpen}
                    onOpenChange={setStartDialogOpen}
                    // Från startsidan startar man alltid en ny annons – det gamla
                    // utkastet rensas, så hela planens tak gäller här.
                    maxImages={maxImages}
                    isFreePlan={activePlan === "free"}
                    onFiles={(files) => void acceptHomeFiles(files)}
                  />
                </Suspense>
              ) : null}

              {images.length > 0 && (
                <p className="mt-4 text-center text-xs text-ocean-200">
                  {lc.saved}{form.title ? ` – ${form.title}` : ""}.{" "}
                  <Link to="/skapa" className="font-semibold text-ocean-300 underline">
                    {lc.continue}
                  </Link>
                </p>
              )}
            </>
          ) : (
            <div
              id="skapa-form"
              {...dropProps}
              className={`mx-auto mt-5 max-w-xl rounded-lg border bg-white/5 p-3 text-left backdrop-blur-sm transition-colors sm:mt-10 sm:p-8 ${
                dropActive ? "border-ocean-300 bg-ocean-300/10" : "border-white/10"
              }`}
            >
              {startDialogOpen ? (
                <Suspense fallback={null}>
                  <StartCaptureDialog
                    open={startDialogOpen}
                    onOpenChange={setStartDialogOpen}
                    maxImages={Math.max(0, maxImages - images.length)}
                    isFreePlan={activePlan === "free"}
                    onFiles={(files) => void processFiles(files)}
                  />
                </Suspense>
              ) : null}

              {prefillNotice && (
                <Alert className="mb-4 border-amber-500/40 bg-amber-500/10 text-foreground [&>svg]:text-amber-600">
                  <Info className="h-4 w-4" />
                  <AlertTitle className="text-sm">{lc.estimate}</AlertTitle>
                  <AlertDescription className="text-xs">{prefillNotice}</AlertDescription>
                </Alert>
              )}

              {preliminaryNote && !highValue && (
                <Alert className="mb-4 border-ocean-300/40 bg-ocean-300/10 text-foreground [&>svg]:text-ocean-300">
                  <Info className="h-4 w-4" />
                  <AlertTitle className="text-sm">{lc.preliminary}</AlertTitle>
                  <AlertDescription className="text-xs">{preliminaryNote}</AlertDescription>
                </Alert>
              )}

              {valuationDraft && testFeatures && !highValue && (
                <Alert className="mb-4 border-primary/40 bg-primary/10 text-foreground [&>svg]:text-primary">
                  <Gem className="h-4 w-4" />
                  <AlertTitle className="text-sm">{lc.deepStarted}</AlertTitle>
                  <AlertDescription className="space-y-2 text-xs">
                    <p>
                      Du har en påbörjad djupvärdering av {valuationDraft.itemName}. Dina svar och
                      bilder ligger kvar.
                    </p>
                    <div className="flex flex-wrap gap-2">
                      <Button
                        type="button"
                        size="sm"
                        onClick={() =>
                          setHighValue({
                            reason: `Fortsätt djupvärderingen av ${valuationDraft.itemName}.`,
                          })
                        }
                      >
                        Fortsätt djupvärderingen
                      </Button>
                      <Button
                        type="button"
                        size="sm"
                        variant="ghost"
                        onClick={() => {
                          clearDeepValuationDraft();
                          setValuationDraft(null);
                        }}
                      >
                        Släng den
                      </Button>
                    </div>
                  </AlertDescription>
                </Alert>
              )}

              {!restored ? (
                <div
                  aria-busy="true"
                  aria-label="Läser in ditt utkast"
                  className="grid gap-3 sm:grid-cols-[minmax(0,2fr)_minmax(15rem,1fr)]"
                >
                  <div className="flex flex-col items-center justify-center rounded-2xl border border-white/10 bg-ocean-800/40 px-4 py-6 text-center sm:px-6 sm:py-12">
                    <Skeleton className="h-5 w-20 rounded-full bg-white/10" />
                    <Skeleton className="mt-4 h-16 w-16 rounded-2xl bg-white/10 sm:mt-6 sm:h-24 sm:w-24" />
                    <Skeleton className="mt-5 h-6 w-40 bg-white/10" />
                    <Skeleton className="mt-2 h-4 w-56 bg-white/10" />
                    <Skeleton className="mt-5 h-11 w-36 rounded-xl bg-white/10" />
                  </div>
                  <div className="flex flex-col gap-3">
                    <Skeleton className="h-24 w-full rounded-2xl bg-white/10" />
                    <Skeleton className="h-24 w-full rounded-2xl bg-white/10" />
                  </div>
                </div>
              ) : prefillLoading ? (
                <div className="flex flex-col items-center justify-center rounded-2xl border border-white/10 bg-ocean-800/40 px-6 py-12 text-center">
                  <Loader2 className="h-8 w-8 animate-spin text-ocean-300" />
                  <p className="mt-4 font-heading text-lg font-bold text-white">{lcm.fetchingItem}</p>
                  <p className="mt-1.5 max-w-xs text-xs leading-relaxed text-ocean-100">
                    {lcm.fetchingItemText}
                  </p>
                </div>
              ) : images.length === 0 ? (
                <div className="grid gap-3 sm:grid-cols-[minmax(0,2fr)_minmax(15rem,1fr)]">
                  <button
                    onClick={() => {
                      // Kameran först – filväljaren finns som alternativ i dialogen.
                      if (isAppMode()) openCamera(null);
                      else setStartDialogOpen(true);
                    }}
                    className="group relative flex w-full flex-col items-center justify-center overflow-hidden rounded-2xl border border-white/10 bg-ocean-800/40 px-4 py-6 text-center transition-all hover:border-ocean-300/40 hover:bg-ocean-800/60 hover:shadow-lg hover:shadow-ocean-900/20 sm:px-6 sm:py-12"
                  >
                    <span className="mb-3 inline-flex items-center rounded-full border border-ocean-300/20 bg-ocean-300/10 px-3 py-1 text-[11px] font-bold uppercase tracking-widest text-ocean-300 sm:mb-5 sm:text-xs">
                      {lcx.step1}
                    </span>

                    <div className="relative mb-4 sm:mb-6">
                      <div className="absolute inset-0 rounded-2xl bg-ocean-300/20 blur-xl transition-opacity group-hover:opacity-100 opacity-60" />
                      <div className="relative grid h-16 w-16 place-items-center rounded-2xl bg-gradient-to-br from-ocean-300 to-ocean-500 shadow-lg shadow-ocean-900/30 transition-transform duration-300 group-hover:rotate-0 rotate-3 sm:h-24 sm:w-24">
                        <Camera className="h-7 w-7 text-ocean-900 sm:h-10 sm:w-10" />
                      </div>
                    </div>

                    <p className="font-heading text-lg font-bold text-white sm:text-2xl">
                      {lcx.shootTitle}
                    </p>
                    <p className="mx-auto mt-1.5 max-w-xs text-xs leading-relaxed text-ocean-100 sm:mt-2 sm:text-sm">
                      {lcx.shootText}
                    </p>

                    <div className="relative mt-4 inline-flex items-center justify-center sm:mt-6">
                      <span className="absolute -inset-1 rounded-2xl bg-ocean-300/40 opacity-0 blur-lg transition-opacity duration-300 group-hover:opacity-100" />
                      <span className="absolute inset-0 rounded-xl bg-ocean-300 opacity-0 transition-opacity group-hover:opacity-100 group-hover:animate-ping-slow" />
                      <span className="relative inline-flex items-center gap-2 overflow-hidden rounded-xl bg-ocean-300 px-6 py-3 font-semibold text-ocean-900 shadow-md ring-2 ring-ocean-300/0 transition-all duration-300 group-hover:-translate-y-0.5 group-hover:scale-105 group-hover:bg-ocean-200 group-hover:shadow-xl group-hover:shadow-ocean-300/30 group-hover:ring-white/50 group-active:translate-y-0 group-active:scale-100">
                        <span className="pointer-events-none absolute inset-y-0 -left-1/3 w-1/3 -skew-x-12 bg-white/50 opacity-0 transition-none group-hover:animate-[shine_0.9s_ease-out] group-hover:opacity-100" />
                        {lcx.startHere}
                        <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                      </span>
                    </div>

                    <p className="mt-4 text-xs font-medium text-ocean-200/70">
                      {lcx.under30}
                    </p>
                  </button>
                  {appMode && (
                    <button
                      onClick={() => openLibrary(null)}
                      className="inline-flex items-center justify-center gap-2 rounded-xl border border-white/10 bg-white/5 px-4 py-2.5 text-sm font-semibold text-ocean-100 transition hover:border-ocean-300/30 hover:bg-white/10"
                    >
                      <Images className="h-4 w-4" />
                      {lcx.fromLibrary}
                    </button>
                  )}
                  <div className="flex flex-col gap-3">
                    {[
                      {
                        to: "/mina-prylar" as const,
                        search: { lage: "film" as const },
                        icon: Video,
                        title: lcx.videoTitle,
                        text: lcx.videoText,
                        plan: "Pro",
                      },
                      {
                        to: "/mina-prylar" as const,
                        search: { lage: "foton" as const },
                        icon: Images,
                        title: lcx.photosTitle,
                        text: lcx.photosText,
                        plan: "Plus",
                      },
                    ].map((card) => (
                      <Link
                        key={card.title}
                        to={card.to}
                        search={card.search}
                        className="group flex flex-1 items-center gap-3 rounded-2xl border border-ocean-300/25 bg-ocean-800/40 px-4 py-4 text-left transition-colors hover:border-ocean-300/50 hover:bg-ocean-800/60"
                      >
                        <span className="grid h-11 w-11 shrink-0 place-items-center rounded-xl bg-ocean-300/15 text-ocean-300">
                          <card.icon className="h-5 w-5" />
                        </span>
                        <span className="min-w-0">
                          <span className="flex flex-wrap items-center gap-2">
                            <span className="font-heading text-base font-bold text-white">
                              {card.title}
                            </span>
                            <span className="rounded-full bg-ocean-300/15 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-ocean-300">
                              {card.plan}
                            </span>
                          </span>
                          <span className="mt-0.5 block text-xs leading-relaxed text-ocean-100">
                            {card.text}
                          </span>
                        </span>
                        <ArrowRight className="ml-auto h-4 w-4 shrink-0 text-ocean-300 transition-transform group-hover:translate-x-0.5" />
                      </Link>
                    ))}
                  </div>
                </div>
              ) : resultLocked ? null : (
                <div className="space-y-4">
                  {aiInsight && (
                    <AiInsightCard
                      insight={aiInsight}
                      busy={loading}
                      onPick={(item) => void handleGenerate(item)}
                      onCleanImage={
                        user
                          ? () => {
                              const index = photoQuality?.imageIndexes[0] ?? 0;
                              setCleanDialog(index);
                              void cleanBackground(index);
                            }
                          : undefined
                      }
                    />
                  )}
                  {photoQuality && (
                    <div ref={photoNoticeRef} className="scroll-mt-24">
                      <PhotoQualityNotice
                        quality={photoQuality}
                        flaggedImages={photoQuality.imageIndexes
                          .map((i) => images[i])
                          .map((img, position) =>
                            img
                              ? {
                                  index: photoQuality.imageIndexes[position],
                                  src: `data:${img.mimeType};base64,${img.imageBase64}`,
                                }
                              : null,
                          )
                          .filter((item): item is { index: number; src: string } => item !== null)}
                        onRetake={(index) => {
                          pendingAdRef.current = null;
                          openCamera(index);
                        }}
                        onCleanBackground={
                          user
                            ? () => {
                                // Öppna studion – bilden byts först när användaren godkänner.
                                const index = photoQuality.imageIndexes[0] ?? 0;
                                setCleanDialog(index);
                                void cleanBackground(index);
                              }
                            : undefined
                        }
                        onContinueAnyway={() => {
                          pendingAdRef.current?.();
                          pendingAdRef.current = null;
                          setPhotoQuality(null);
                        }}
                        onDismiss={() => setPhotoQuality(null)}
                      />
                    </div>
                  )}
                  {contactFinding && (
                    <Alert className="border-amber-500/50 bg-amber-500/10 text-foreground [&>svg]:text-amber-500">
                      <AlertTriangle className="h-4 w-4" />
                      <AlertTitle>
                        Bild {contactFinding.imageIndex + 1} innehåller kontaktinformation
                      </AlertTitle>
                      <AlertDescription>
                        Vi ser tydligt {contactFinding.evidence}. Ta om bilden utan kontaktuppgiften
                        — all kontakt ska ske via meddelanden här.
                      </AlertDescription>
                    </Alert>
                  )}
                  <div className="relative flex aspect-video w-full items-center justify-center overflow-hidden rounded-md bg-ocean-900/50">
                    <img
                      src={`data:${images[0].mimeType};base64,${images[0].imageBase64}`}
                      alt={flowText.imageAlt}
                      className="max-h-full max-w-full object-contain"
                    />
                    <button
                      type="button"
                      onClick={() => rotateImage(0)}
                      disabled={rotating !== null}
                      aria-label={flowText.rotate}
                      className="absolute bottom-2 right-2 inline-flex items-center gap-1.5 rounded-md bg-ocean-900/80 px-2.5 py-1.5 text-xs font-medium text-white transition-colors hover:bg-ocean-300 hover:text-ocean-900 disabled:opacity-60"
                    >
                      {rotating === 0 ? (
                        <Loader2 className="h-3.5 w-3.5 animate-spin" />
                      ) : (
                        <RotateCw className="h-3.5 w-3.5" />
                      )}
                      Rotera
                    </button>
                    {user && (
                      <button
                        type="button"
                        onClick={() => setCleanDialog(0)}
                        disabled={cleaning !== null}
                        aria-label="Bildstudio – rensa bakgrund eller fixa till bilden"
                        title="Bildstudio"
                        className="absolute bottom-2 left-2 inline-flex h-8 w-8 items-center justify-center rounded-full bg-ocean-900/80 text-white transition-colors hover:bg-ocean-300 hover:text-ocean-900 disabled:opacity-60"
                      >
                        {cleaning === 0 ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <Wand2 className="h-4 w-4" />
                        )}
                      </button>
                    )}
                  </div>
                  {user &&
                    (() => {
                      // Studion kan gälla vilken bild som helst – inte bara den första.
                      const si = cleanDialog ?? 0;
                      const base = images[si] ?? images[0];
                      const prefix = `${si}:`;
                      if (cleanDialog === null) return null;
                      return (
                        <Suspense fallback={null}>
                          <ImageStudioDialog
                            open={cleanDialog !== null}
                            onOpenChange={(open) => {
                              if (open) setCleanDialog(si);
                              else discardStudioSelection(si);
                            }}
                            mode={cleanMode[si] ?? "background"}
                            quota={cleanQuota}
                            busy={cleaning !== null}
                            hasClean={Object.keys(cleanResults).some((k) => k.startsWith(prefix))}
                            variant={cleanVariants[`${si}:${cleanMode[si] ?? "background"}`] ?? 1}
                            savedModes={(["background", "studio", "angle"] as const).filter((m) =>
                              Object.keys(cleanResults).some((k) => k.startsWith(`${si}:${m}:`)),
                            )}
                            originalSrc={
                              cleanOriginals[si]
                                ? `data:${cleanOriginals[si].mimeType};base64,${cleanOriginals[si].imageBase64}`
                                : `data:${base.mimeType};base64,${base.imageBase64}`
                            }
                            resultSrc={(() => {
                              const preview = studioPreview(si) ?? base;
                              return `data:${preview.mimeType};base64,${preview.imageBase64}`;
                            })()}
                            versions={Object.entries(cleanResults)
                              .filter(([k]) => k.startsWith(prefix))
                              .map(([k, v]) => {
                                const [, m, num] = k.split(":");
                                return {
                                  key: k,
                                  mode: (m === "studio" || m === "angle"
                                    ? m
                                    : "background") as CleanMode,
                                  variant: Number(num ?? 1),
                                  src: `data:${v.mimeType};base64,${v.imageBase64}`,
                                };
                              })
                              .sort((a, b) =>
                                a.mode === b.mode
                                  ? a.variant - b.variant
                                  : a.mode < b.mode
                                    ? -1
                                    : 1,
                              )}
                            activeKey={studioSelection[si] ?? null}
                            dirty={!!studioDirty[si]}
                            rotating={rotating === si}
                            subject={studioInfo[si]?.subjectSv ?? null}
                            analyzing={analyzingStudio === si}
                            onReanalyze={() => void runStudioAnalysis(si, true)}
                            error={error}
                            warning={cleanWarning}
                            variantNote={variantNote}
                            onRun={(m, v) => void cleanBackground(si, v, m)}
                            onPick={(m, v) => void cleanBackground(si, v, m)}
                            onRotate={(deg) => void rotateStudioImage(si, deg)}
                            onUndo={() => undoClean(si)}
                            onApply={() => applyStudioSelection(si)}
                            onDiscard={() => discardStudioSelection(si)}
                          />
                        </Suspense>
                      );
                    })()}

                  {cleanDebug && (
                    <div className="-mt-1 rounded-md border border-ocean-300/30 bg-ocean-900/40 p-3 text-xs text-ocean-100/90">
                      <p className="font-semibold text-ocean-100">Debug (endast admin)</p>
                      <p className="mt-1">
                        {cleanDebug.model} · {cleanDebug.inputTokens.toLocaleString("sv-SE")} tokens
                        in / {cleanDebug.outputTokens.toLocaleString("sv-SE")} ut ·{" "}
                        {cleanDebug.credits.toFixed(4).replace(".", ",")} krediter ·{" "}
                        {formatSek(cleanDebug.credits * DEFAULT_CREDIT_SEK, 3)} ·{" "}
                        {(cleanDebug.durationMs / 1000).toFixed(1).replace(".", ",")} s
                      </p>
                    </div>
                  )}
                  <div>
                    <div className="grid grid-cols-3 gap-3">
                      {Array.from({ length: Math.min(images.length + 1, maxImages) }).map(
                        (_, i) => {
                          const img = images[i];
                          if (img) {
                            return (
                              <div
                                key={i}
                                {...imageDrag.tileProps(i)}
                                className={`group relative flex aspect-square cursor-grab select-none items-center justify-center overflow-hidden rounded-md border bg-ocean-900/50 ${
                                  imageDrag.dragIndex === i
                                    ? "border-ocean-300 opacity-60"
                                    : imageDrag.overIndex === i && imageDrag.dragIndex !== null
                                      ? "border-ocean-300 ring-2 ring-ocean-300"
                                      : photoQuality?.imageIndexes.includes(i)
                                        ? `border-transparent ring-2 ${
                                            photoQuality.verdict === "unusable"
                                              ? "ring-destructive"
                                              : "ring-amber-400"
                                          }`
                                        : "border-white/20"
                                }`}
                              >
                                <img
                                  src={`data:${img.mimeType};base64,${img.imageBase64}`}
                                  alt={`Bild ${i + 1}`}
                                  draggable={false}
                                  className="h-full w-full object-cover"
                                />
                                <span className="absolute bottom-1 left-1 rounded-sm bg-ocean-900/70 px-1.5 text-[10px] font-medium text-ocean-100">
                                  {i === 0 ? "Huvudbild" : i + 1}
                                </span>
                                <button
                                  type="button"
                                  onClick={() => rotateImage(i)}
                                  disabled={rotating !== null}
                                  aria-label={`Rotera bild ${i + 1}`}
                                  className="absolute left-1 top-1 rounded-sm bg-ocean-900/80 p-1 text-white transition-colors hover:bg-ocean-300 hover:text-ocean-900 disabled:opacity-60"
                                >
                                  {rotating === i ? (
                                    <Loader2 className="h-3 w-3 animate-spin" />
                                  ) : (
                                    <RotateCw className="h-3 w-3" />
                                  )}
                                </button>
                                <button
                                  type="button"
                                  onClick={() => confirmRemoveImage(i)}
                                  aria-label={`Ta bort bild ${i + 1}`}
                                  className="absolute right-1 top-1 rounded-sm bg-ocean-900/80 p-1 text-white transition-colors hover:bg-destructive"
                                >
                                  <X className="h-3 w-3" />
                                </button>
                                <div className="absolute bottom-1 right-1 flex gap-1">
                                  <button
                                    type="button"
                                    onClick={() => moveImage(i, -1)}
                                    disabled={i === 0}
                                    aria-label={`Flytta bild ${i + 1} bakåt`}
                                    className="rounded-sm bg-ocean-900/80 p-1 text-white transition-colors hover:bg-ocean-300 hover:text-ocean-900 disabled:opacity-40"
                                  >
                                    <ArrowLeft className="h-3 w-3" />
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => moveImage(i, 1)}
                                    disabled={i === images.length - 1}
                                    aria-label={`Flytta bild ${i + 1} framåt`}
                                    className="rounded-sm bg-ocean-900/80 p-1 text-white transition-colors hover:bg-ocean-300 hover:text-ocean-900 disabled:opacity-40"
                                  >
                                    <ArrowRight className="h-3 w-3" />
                                  </button>
                                </div>
                              </div>
                            );
                          }
                          const isNext = i === images.length;
                          return (
                            <button
                              key={i}
                              type="button"
                              disabled={!isNext}
                              onClick={() => openLibrary(null)}
                              className={`flex aspect-square flex-col items-center justify-center gap-1 rounded-md border-2 border-dashed transition-colors ${
                                isNext
                                  ? "border-ocean-300/40 text-ocean-100 hover:border-ocean-300 hover:bg-ocean-700/20 hover:text-white"
                                  : "border-white/10 text-ocean-100/40"
                              }`}
                            >
                              <Plus className="h-4 w-4" />
                              <span className="text-[10px]">{lcm.add}</span>
                            </button>
                          );
                        },
                      )}
                    </div>
                    <DragGhost
                      src={
                        imageDrag.dragIndex !== null && images[imageDrag.dragIndex]
                          ? `data:${images[imageDrag.dragIndex].mimeType};base64,${images[imageDrag.dragIndex].imageBase64}`
                          : null
                      }
                      pos={imageDrag.dragPos}
                    />
                  </div>

                  <div
                    ref={videoCardRef}
                    className="scroll-mt-24 rounded-md border border-white/10 bg-ocean-900/30 p-3"
                  >
                    {canVideo ? (
                      video ? (
                        <div className="space-y-2">
                          <div className="flex items-center justify-between gap-3">
                            <span className="flex min-w-0 items-center gap-2 text-sm text-ocean-100">
                              <Video className="h-4 w-4 shrink-0 text-ocean-300" />
                              <span className="truncate">{video.name}</span>
                            </span>
                            <button
                              type="button"
                              onClick={() => setVideo(null)}
                              className="rounded-sm p-1 text-ocean-100 transition-colors hover:text-white"
                              aria-label="Ta bort videon"
                            >
                              <X className="h-4 w-4" />
                            </button>
                          </div>
                          <video
                            ref={startMuted}
                            src={adVideoSrc(video.path)}
                            poster={video.poster ?? undefined}
                            controls
                            preload="metadata"
                            playsInline
                            className="aspect-video w-full rounded-md border border-white/10 bg-black object-contain"
                          />
                          <p className="text-[11px] text-ocean-100/70">
                            Första bildrutan visas som omslag – tryck på play för att se hela
                            videon. Ljudet finns kvar men startar avstängt.
                          </p>
                        </div>
                      ) : (
                        <button
                          type="button"
                          onClick={() => {
                            void (async () => {
                              const { isAppMode, recordNativeVideo } = await import(
                                "@/app-bridge/core/wrapper",
                              );
                              if (isAppMode()) {
                                const clip = await recordNativeVideo();
                                if (clip) {
                                  void processVideoFile(clip);
                                  return;
                                }
                              }
                              videoInputRef.current?.click();
                            })();
                          }}
                          disabled={videoUploading || !online}
                          className="flex w-full items-center justify-center gap-2 text-sm font-medium text-ocean-100 transition-colors hover:text-white disabled:opacity-60"
                        >
                          {videoUploading ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <Video className="h-4 w-4" />
                          )}
                          {videoUploading
                            ? (videoStatus ?? "Laddar upp video...")
                            : !online
                              ? "Video kräver internet"
                              : "Lägg till video (Pro)"}
                        </button>
                      )
                    ) : (
                      <Link
                        to="/priser"
                        search={{ retur: returPath }}
                        onClick={saveBeforeLeaving}
                        className="flex items-center justify-center gap-2 text-sm text-ocean-100 transition-colors hover:text-white"
                      >
                        <Crown className="h-4 w-4 text-ocean-300" />
                        Video på annonsen ingår i Pro
                      </Link>
                    )}
                  </div>

                  {!hasDraft && !published && (
                    <div className="space-y-2">
                      <div className="flex gap-3">
                        <button
                          onClick={() => void handleGenerate()}
                          disabled={loading || !online}
                          className="inline-flex flex-1 items-center justify-center gap-2 rounded-lg bg-ocean-300 px-5 py-3 font-heading font-semibold text-ocean-900 transition-colors hover:bg-ocean-300/90 disabled:opacity-60"
                        >
                          {loading ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <Sparkles className="h-4 w-4" />
                          )}
                          {loading ? "Skriver annonstexten..." : "Skriv annonstexten"}
                        </button>
                        <button
                          onClick={() => setConfirmReset(true)}
                          className="inline-flex items-center justify-center rounded-lg border border-ocean-300/30 bg-ocean-700/30 px-4 py-3 font-medium text-white transition-colors hover:bg-ocean-700/50"
                          aria-label="Börja om"
                        >
                          <RefreshCw className="h-4 w-4" />
                        </button>
                      </div>
                      {!online && (
                        <p className="text-center text-xs text-ocean-100/80">
                          Du är offline. Bilderna är sparade här i telefonen — tryck på “Skriv
                          annonstexten” så fort du är uppkopplad igen.
                        </p>
                      )}
                    </div>
                  )}
                </div>
              )}
              <input
                ref={fileInputRef}
                type="file"
                accept={IMAGE_ACCEPT}
                multiple
                className="hidden"
                onChange={handleFileChange}
              />
              <input
                ref={videoInputRef}
                type="file"
                accept="video/*"
                className="hidden"
                onChange={handleVideoChange}
              />

              {uploadProgress && (
                <div className="mt-4" aria-live="polite">
                  <div className="flex items-center justify-between text-xs text-ocean-100">
                    <span className="inline-flex items-center gap-2">
                      <Loader2 className="h-3.5 w-3.5 animate-spin text-ocean-300" />
                      Laddar upp {Math.min(uploadProgress.done + 1, uploadProgress.total)} av{" "}
                      {uploadProgress.total} {uploadProgress.total === 1 ? "bild" : "bilder"}...
                    </span>
                    <span className="font-medium text-ocean-300">{uploadProgress.pct}%</span>
                  </div>
                  <div className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-white/10">
                    <div
                      className="h-full rounded-full bg-ocean-300 transition-all duration-300 ease-out"
                      style={{ width: `${uploadProgress.pct}%` }}
                    />
                  </div>
                </div>
              )}

              {hasDraft && !published && resultLocked && (
                <LockedResultPanel
                  ref={reviewRef}
                  thumbs={images.map((i) => `data:${i.mimeType};base64,${i.imageBase64}`)}
                  categoryLabel={CATEGORIES.find((c) => c.slug === form.category)?.label ?? null}
                  title={form.title}
                  onRegister={() => {
                    setAuthMode("register");
                    setAuthOpen(true);
                  }}
                  onLogin={() => {
                    setAuthMode("login");
                    setAuthOpen(true);
                  }}
                />
              )}

              {hasDraft && !published && !resultLocked && (
                <form onSubmit={handlePublish} className="mt-6 space-y-6">
                  {/* Steg 1 — annonsen */}
                  <div
                    ref={reviewRef}
                    className="scroll-mt-24 space-y-4 rounded-md border border-white/10 bg-white/5 p-5"
                  >
                    <div className="flex items-start justify-between gap-3">
                       <StepLabel step={1} title={lc.review} />
                      <button
                        type="button"
                        onClick={() => setRegenerateOpen(true)}
                        disabled={loading}
                        className="inline-flex shrink-0 items-center gap-2 rounded-lg border border-ocean-300/30 bg-ocean-700/30 px-3 py-2 text-sm font-medium text-white transition-colors hover:bg-ocean-700/50 disabled:opacity-60"
                      >
                        <RefreshCw className="h-4 w-4" />
                         <span className="hidden sm:inline">{lc.reanalyse}</span>
                      </button>
                    </div>
                    {splitInfo && (
                      <SplitSuggestion
                        cohesion={splitInfo.cohesion}
                        groups={splitInfo.groups.map((g) => ({
                          label: g.label,
                          count: g.imageIndexes.length,
                        }))}
                        onSplit={handleSplit}
                        onKeep={handleKeepAsOne}
                        quotaNote={
                          splitInfo.cohesion === "mixed"
                            ? "Varje annons räknas mot din månadsgräns."
                            : null
                        }
                      />
                    )}
                     <FieldWrap label={lc.title} htmlFor="f-title" error={fieldError("title")}>
                      <Input
                        id="f-title"
                        value={form.title}
                         placeholder={lc.titlePlaceholder}
                        maxLength={AD_TITLE_MAX}
                        onChange={(e) =>
                          setForm({ ...form, title: e.target.value.slice(0, AD_TITLE_MAX) })
                        }
                        className={cn(
                          "bg-ocean-900/40 text-white placeholder:text-ocean-100/60",
                          fieldError("title")
                            ? "border-amber-300/70 ring-1 ring-amber-300/40"
                            : "border-white/20",
                        )}
                      />
                      <p className="mt-1 text-xs text-ocean-100/70">
                         {form.title.length}/{AD_TITLE_MAX} {lc.chars}
                      </p>
                    </FieldWrap>
                    <FieldWrap
                       label={lc.description}
                      htmlFor="f-desc"
                      error={fieldError("description")}
                    >
                      <Textarea
                        id="f-desc"
                        rows={5}
                         placeholder={lc.descriptionPlaceholder}
                        value={form.description}
                        onChange={(e) => setForm({ ...form, description: e.target.value })}
                        className={cn(
                          "bg-ocean-900/40 text-white placeholder:text-ocean-100/60",
                          fieldError("description")
                            ? "border-amber-300/70 ring-1 ring-amber-300/40"
                            : "border-white/20",
                        )}
                      />
                    </FieldWrap>
                    <ModerationNotice
                      title={form.title}
                      description={form.description}
                      result={
                        moderation &&
                        moderation.title === form.title &&
                        moderation.description === form.description
                          ? moderation.check
                          : null
                      }
                      onFix={(clean) => {
                        setModeration(null);
                        setForm({ ...form, title: clean.title, description: clean.description });
                      }}
                      onFixAndSubmit={(clean) => {
                        setModeration(null);
                        setForm({ ...form, title: clean.title, description: clean.description });
                        void runPublish(clean);
                      }}
                    />
                    {auctionsOn && (
                      <div className="rounded-lg border border-white/10 bg-ocean-900/30 p-4">
                        <p className="flex items-center gap-2 font-heading text-sm font-semibold text-ocean-100">
                           {lc.howSell}
                          {auctionsOn && <AuctionDevBadge />}
                        </p>
                        <div className="mt-3 grid gap-2 sm:grid-cols-2">
                          {[
                            {
                              kind: "fixed" as const,
                               title: lc.fixed,
                               text: lc.fixedText,
                            },
                            {
                              kind: "auction" as const,
                               title: lc.auction,
                               text: lc.auctionText,
                            },
                          ]
                            .filter((opt) => auctionsOn || opt.kind === "fixed")
                            .map((opt) => (
                              <button
                                key={opt.kind}
                                type="button"
                                onClick={() => setSaleKind(opt.kind)}
                                className={cn(
                                  "rounded-lg border p-3 text-left transition-colors",
                                  saleKind === opt.kind
                                    ? "border-ocean-300 bg-ocean-300/10"
                                    : "border-white/15 hover:border-white/30",
                                )}
                              >
                                <span className="flex items-center gap-2">
                                  <span className="font-heading text-sm font-semibold text-white">
                                    {opt.title}
                                  </span>
                                  {saleAdvice?.kind === opt.kind && (
                                    <span className="rounded-full bg-ocean-300/20 px-2 py-0.5 text-[10px] font-medium text-ocean-100">
                                       {lc.suggestion}
                                    </span>
                                  )}
                                </span>
                                <span className="mt-1 block text-xs leading-relaxed text-ocean-100/80">
                                  {opt.text}
                                </span>
                              </button>
                            ))}
                        </div>
                        {saleAdvice?.reason && (
                          <p className="mt-2 text-xs leading-relaxed text-ocean-100/80">
                            Vi föreslår {saleAdvice.kind === "auction" ? "auktion" : "fast pris"}:{" "}
                            {saleAdvice.reason}
                          </p>
                        )}
                        {auctionsOn && saleKind === "auction" && (
                          <div className="mt-4 space-y-4">
                            {/* Prisfälten i en egen ruta – båda kolumnerna lika höga. */}
                            <div className="rounded-xl border border-white/10 bg-ocean-900/30 p-4">
                              <div className="grid items-start gap-4 sm:grid-cols-2">
                                <FieldWrap
                                   label={`${lc.startPrice} (kr)`}
                                  htmlFor="f-price"
                                  help={
                                    <span className="text-xs text-ocean-100/70">
                                       {lc.lowestBid}
                                    </span>
                                  }
                                  error={fieldError("price")}
                                >
                                  <Input
                                    id="f-price"
                                    type="number"
                                    min={AUCTION_MIN_START_PRICE}
                                    placeholder="T.ex. 1 500"
                                    value={form.price}
                                    onChange={(e) => setForm({ ...form, price: e.target.value })}
                                    className={cn(
                                      "bg-ocean-900/40 text-white",
                                      fieldError("price") || valueWarning
                                        ? "border-amber-300/70 ring-1 ring-amber-300/40"
                                        : "border-white/20",
                                    )}
                                  />
                                </FieldWrap>

                                <FieldWrap
                                   label={`${lc.reserve} (kr)`}
                                  htmlFor="f-reserve"
                                  help={
                                    <span className="text-xs text-ocean-100/70">
                                       {lc.lowestSell}
                                    </span>
                                  }
                                >
                                  <Input
                                    id="f-reserve"
                                    type="number"
                                    min={auctionReserveFloor(form.price)}
                                    value={reservePrice}
                                    onChange={(e) => setReservePrice(e.target.value)}
                                    placeholder={form.price || "0"}
                                    className={cn(
                                      "bg-ocean-900/40 text-white",
                                      reserveWarning
                                        ? "border-amber-300/70 ring-1 ring-amber-300/40"
                                        : "border-white/20",
                                    )}
                                  />
                                </FieldWrap>
                              </div>

                              {/* Rekommendationen i full bredd – inget hoppar i rutnätet. */}
                              {reserveRecommendation ? (
                                (Math.round(Number(reservePrice)) || 0) >=
                                reserveRecommendation.amount ? (
                                  reserveAccepted === reserveRecommendation.amount ? (
                                    <p className="mt-3 flex items-center gap-2 text-xs text-ocean-100/80">
                                      <Check className="h-3.5 w-3.5 shrink-0 text-ocean-300" />
                                      Reservationspris satt till{" "}
                                      {formatKr(reserveRecommendation.amount)}.
                                    </p>
                                  ) : null
                                ) : (
                                  <div className="mt-3 flex flex-wrap items-center justify-between gap-2 rounded-lg border border-white/10 bg-ocean-900/50 px-3 py-2">
                                    <span className="min-w-0 text-xs text-ocean-100/80">
                                      Rekommenderat:{" "}
                                      <span className="font-semibold text-white">
                                        {formatKr(reserveRecommendation.amount)}
                                      </span>
                                      <span className="text-ocean-100/60">
                                        {" "}
                                        · {reserveRecommendation.reason}
                                      </span>
                                    </span>
                                    <button
                                      type="button"
                                      onClick={() => {
                                        setReservePrice(String(reserveRecommendation.amount));
                                        setReserveAccepted(reserveRecommendation.amount);
                                      }}
                                      className="shrink-0 rounded-md border border-ocean-300/50 bg-ocean-300/10 px-2.5 py-1 text-xs font-medium text-ocean-100 transition hover:bg-ocean-300/20"
                                    >
                                       {lc.use}
                                    </button>
                                  </div>
                                )
                              ) : null}

                              {/* Varningarna i egen ruta i full bredd. */}
                              {(valueWarning || reserveWarning) && (
                                <div className="mt-3 flex gap-2 rounded-lg border border-amber-300/40 bg-amber-300/10 p-3">
                                  <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-200" />
                                  <div className="space-y-1.5">
                                    {valueWarning && (
                                      <p className="text-xs font-medium leading-relaxed text-amber-100">
                                        {valueWarning.message}{" "}
                                        <button
                                          type="button"
                                          className="underline underline-offset-2"
                                          onClick={() =>
                                            setForm({
                                              ...form,
                                              price: String(valueWarning.suggested),
                                            })
                                          }
                                        >
                                          Höj till {formatKr(valueWarning.suggested)}
                                        </button>
                                      </p>
                                    )}
                                    {reserveWarning && (
                                      <p className="text-xs font-medium leading-relaxed text-amber-100">
                                        {reserveWarning.message}
                                        {reserveWarning.suggested ? (
                                          <>
                                            {" "}
                                            <button
                                              type="button"
                                              className="underline underline-offset-2"
                                              onClick={() =>
                                                setReservePrice(String(reserveWarning.suggested))
                                              }
                                            >
                                              Sätt {formatKr(reserveWarning.suggested)}
                                            </button>
                                          </>
                                        ) : null}
                                      </p>
                                    )}
                                  </div>
                                </div>
                              )}
                            </div>

                             <FieldWrap label={lc.duration} htmlFor="f-days">
                              <div className="grid grid-cols-3 gap-2">
                                {AUCTION_DAYS.map((d) => (
                                  <button
                                    key={d}
                                    type="button"
                                    onClick={() => setAuctionDays(d)}
                                    className={cn(
                                      "h-10 rounded-md border text-sm font-semibold transition-colors",
                                      auctionDays === d
                                        ? "border-ocean-300 bg-ocean-300 text-ocean-900"
                                        : "border-white/20 text-ocean-100 hover:border-white/40",
                                    )}
                                  >
                                     {d} {lc.days}
                                  </button>
                                ))}
                              </div>
                              <p className="mt-1.5 text-xs leading-relaxed text-ocean-100/70">
                                Kort tid ger snabb affär, längre tid når fler budgivare. 7 dagar
                                passar de flesta prylar – 14 dagar för dyrare och ovanligare saker.
                              </p>
                            </FieldWrap>

                            <p className="text-xs leading-relaxed text-ocean-100/80">
                              Når högsta budet inte ditt reservationspris säljs prylen inte. Kommer
                              ett bud in de sista fem minuterna förlängs auktionen fem minuter.
                            </p>
                          </div>
                        )}
                      </div>
                    )}
                    <div
                      className={cn(
                        "grid items-start gap-4",
                        saleKind === "auction" ? "sm:grid-cols-1" : "sm:grid-cols-2",
                      )}
                    >
                      {saleKind === "fixed" && (
                         <FieldWrap label={`${lc.price} (kr)`} htmlFor="f-price" error={fieldError("price")}>
                          <Input
                            id="f-price"
                            type="number"
                            min={1}
                            placeholder="T.ex. 1 500"
                            value={form.price}
                            onChange={(e) => setForm({ ...form, price: e.target.value })}
                            className={cn(
                              "bg-ocean-900/40 text-white",
                              fieldError("price")
                                ? "border-amber-300/70 ring-1 ring-amber-300/40"
                                : "border-white/20",
                            )}
                          />
                        </FieldWrap>
                      )}
                       <FieldWrap label={lc.category} htmlFor="f-cat" error={fieldError("category")}>
                        <Select
                          value={form.category}
                          onValueChange={(v) => setForm({ ...form, category: v })}
                        >
                          <SelectTrigger
                            id="f-cat"
                            className={cn(
                              "h-10 bg-ocean-900/40 text-white shadow-none focus:ring-ocean-300",
                              fieldError("category")
                                ? "border-amber-300/70 ring-1 ring-amber-300/40"
                                : "border-white/20",
                            )}
                          >
                             <SelectValue placeholder={lc.chooseCategory} />
                          </SelectTrigger>
                          <SelectContent>
                            {CATEGORIES.map((c) => (
                              <SelectItem key={c.slug} value={c.slug}>
                                <span className="inline-flex items-center gap-2">
                                  <c.icon className="h-4 w-4 text-muted-foreground" />
                                  {c.label}
                                </span>
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </FieldWrap>
                    </div>
                    {saleKind === "fixed" && (
                      <div className="rounded-lg border border-white/10 bg-ocean-900/30 p-4">
                        <div className="grid grid-cols-[minmax(0,1fr)_auto] items-center gap-4">
                          <div className="min-w-0">
                            <p className="font-heading text-sm font-semibold text-ocean-100">
                               {lc.offers}
                            </p>
                            <p className="mt-1 text-xs leading-relaxed text-ocean-100/80">
                               {lc.offersText}
                            </p>
                          </div>
                          <Switch
                            checked={acceptsOffers}
                            onCheckedChange={setAcceptsOffers}
                            aria-label="Ta emot bud"
                            className="shrink-0"
                          />
                        </div>
                        {offersAdvice && (
                          <p className="mt-2 text-xs leading-relaxed text-ocean-100/80">
                            {lcm.ourSuggestion}: {offersAdvice}
                          </p>
                        )}
                      </div>
                    )}
                    <div className="grid items-start gap-4 sm:grid-cols-[minmax(0,1.2fr)_minmax(0,0.8fr)]">
                      <FieldWrap
                         label={lc.locality}
                        htmlFor="f-loc"
                        help={<LocalityHelp />}
                        error={fieldError("location")}
                      >
                        <div className="grid grid-cols-[minmax(0,1fr)_auto] items-center gap-2">
                          <div className="min-w-0">
                            <LocalityCombobox
                              id="f-loc"
                              country={adCountry}
                              value={form.location}
                              onChange={(location) => {
                                setLocationPrefilled(false);
                                setForm({ ...form, location });
                              }}
                              className={cn(
                                "bg-ocean-900/40 text-white hover:bg-ocean-900/60 hover:text-white",
                                fieldError("location")
                                  ? "border-amber-300/70 ring-1 ring-amber-300/40"
                                  : "border-white/20",
                              )}
                            />
                          </div>
                          <AdCountryPicker
                            value={adCountry}
                            homeCountry={sellCountry}
                            onChange={(code) => {
                              setAdCountry(code);
                              setLocationPrefilled(false);
                              setForm((f) => ({ ...f, location: "" }));
                            }}
                            className="w-36 sm:w-40"
                          />
                        </div>
                        <button
                          type="button"
                          onClick={useMyPosition}
                          disabled={geoState.status === "loading"}
                          className="mt-1.5 inline-flex items-center gap-1.5 text-xs font-medium text-ocean-300 underline-offset-2 hover:underline disabled:opacity-50"
                        >
                          {geoState.status === "loading" ? (
                            <Loader2 className="h-3.5 w-3.5 animate-spin" />
                          ) : (
                            <LocateFixed className="h-3.5 w-3.5" />
                          )}
                           {lc.locate}
                        </button>
                        {permissionPrimer.dialog}
                        {geoState.message && (
                          <p
                            className={cn(
                              "mt-1 text-xs",
                              geoState.status === "error" ? "text-amber-200" : "text-ocean-100",
                            )}
                          >
                            {geoState.message}
                          </p>
                        )}

                        {locationPrefilled && form.location && (
                          <p className="mt-1.5 flex items-center gap-1.5 text-xs text-ocean-100">
                            <MapPin className="h-3.5 w-3.5 shrink-0 text-ocean-300" />
                            Förvalt från din sparade adress – du kan ändra den.
                          </p>
                        )}
                      </FieldWrap>

                      <FieldWrap label="Gata eller område (valfritt)" htmlFor="f-addr">
                        <Input
                          id="f-addr"
                          placeholder="T.ex. Majorna"
                          value={form.address}
                          onChange={(e) => setForm({ ...form, address: e.target.value })}
                          className="border-white/20 bg-ocean-900/40 text-white placeholder:text-ocean-100/60"
                        />
                      </FieldWrap>
                    </div>
                    <Suspense fallback={null}>
                      <SavedPlacesPicker
                        dark
                        highlight={!!fieldError("location")}
                        location={form.location}
                        address={form.address}
                        onSelect={(p) =>
                          setForm({ ...form, location: p.location, address: p.address })
                        }
                      />
                    </Suspense>

                    <p className="flex items-start gap-2 text-xs text-ocean-100">
                      <MapPin className="mt-0.5 h-3.5 w-3.5 shrink-0 text-ocean-300" />
                      Vi visar platsen på en karta i annonsen. Ange bara så exakt som du är bekväm
                      med.
                    </p>
                  </div>

                  {/* Steg 2 — konto + kontakt */}
                  {authLoading ? (
                    <div
                      className="h-40 animate-pulse rounded-md border border-ocean-300/30 bg-ocean-700/25"
                      aria-hidden
                    />
                  ) : !user ? (
                    <div className="space-y-4 rounded-md border border-ocean-300/30 bg-ocean-700/25 p-5">
                      <StepLabel step={2} title={lc.login} />
                      <div className="flex items-start gap-3 text-sm text-ocean-100">
                        <Lock className="mt-0.5 h-4 w-4 shrink-0 text-ocean-300" />
                        <p>
                          Din annons är kopplad till ditt konto så att du kan ändra pris och text i
                          efterhand. Vi hämtar e-postadressen från kontot — då slipper du fylla i
                          den manuellt.
                        </p>
                      </div>
                      <div className="flex flex-col gap-3 sm:flex-row">
                        <button
                          type="button"
                          onClick={() => openAuth("register")}
                          className="inline-flex flex-1 items-center justify-center gap-2 rounded-lg bg-ocean-300 px-5 py-3 font-heading font-semibold text-ocean-900 transition-colors hover:bg-ocean-300/90"
                        >
                          Skapa konto
                        </button>
                        <button
                          type="button"
                          onClick={() => openAuth("login")}
                          className="inline-flex flex-1 items-center justify-center gap-2 rounded-lg border border-ocean-300/40 bg-ocean-900/30 px-5 py-3 font-heading font-semibold text-white transition-colors hover:bg-ocean-900/50"
                        >
                          Logga in
                        </button>
                      </div>
                      <p className="flex items-center gap-2 text-xs text-ocean-100">
                        <ShieldCheck className="h-3.5 w-3.5 text-ocean-300" /> Ditt utkast sparas i
                        webbläsaren och finns kvar när du kommit in.
                      </p>
                    </div>
                  ) : (
                    <div className="space-y-4 rounded-md border border-white/10 bg-white/5 p-5">
                      <StepLabel step={2} title="Kontaktuppgifter och publicering" />
                      <div
                        id="f-profil"
                        className={`space-y-3 scroll-mt-28 rounded-md border p-4 transition-colors ${
                          attempted && !form.contactName && !displayNameDraft.trim()
                            ? "border-amber-300/60 bg-amber-200/10 ring-1 ring-amber-300/40"
                            : "border-white/10 bg-ocean-900/30"
                        }`}
                      >
                        <div className="flex flex-wrap items-center justify-between gap-2">
                          <span className="text-xs font-semibold uppercase tracking-wide text-ocean-100/70">
                            Dina kontaktuppgifter
                          </span>
                          <Link
                            to="/profil"
                            className="text-xs text-ocean-300 underline underline-offset-2"
                          >
                            Ändra på min profil
                          </Link>
                        </div>
                        {form.contactName ? (
                          <>
                            <dl className="divide-y divide-white/10 overflow-hidden rounded-md border border-white/10 text-sm text-ocean-50">
                              <div className="flex items-baseline gap-3 px-3 py-2">
                                <dt className="w-28 shrink-0 text-[11px] uppercase tracking-wide text-ocean-100/70">
                                  Visningsnamn
                                </dt>
                                <dd
                                  className="min-w-0 flex-1 truncate text-right font-medium"
                                  title={form.contactName}
                                >
                                  {form.contactName}
                                </dd>
                              </div>
                            </dl>
                            <p className="flex items-start gap-2 text-xs leading-relaxed text-ocean-100">
                              <ShieldCheck className="mt-0.5 h-3.5 w-3.5 shrink-0 text-ocean-300" />
                              <span>
                                Bara ditt visningsnamn syns i annonsen. Dina kontaktuppgifter hämtas
                                från din profil och visas aldrig för andra – köpare kontaktar dig
                                via meddelanden {surfaceText.inPlace}.
                              </span>
                            </p>
                          </>
                        ) : contactDefaultsPending && user ? (
                          <p className="text-xs text-ocean-100/70">{lcm.fetchingDetails}</p>
                        ) : (
                          <Suspense fallback={null}>
                            <DisplayNameSetup
                              registerSave={registerDisplayNameSave}
                              onDraftChange={handleDisplayNameDraft}
                              onSaved={(n) => setForm((f) => ({ ...f, contactName: n }))}
                            />
                          </Suspense>
                        )}
                      </div>

                      {peopleInImages && (
                        <label
                          id="f-personer"
                          className={`flex scroll-mt-28 items-start gap-3 rounded-md p-3 text-xs leading-relaxed text-ocean-50 transition-colors ${
                            attempted && !peopleConsent
                              ? "border border-amber-300/60 bg-amber-200/10 ring-1 ring-amber-300/40"
                              : "border border-white/10 bg-ocean-900/30"
                          }`}
                        >
                          <Checkbox
                            checked={peopleConsent}
                            onCheckedChange={(v) => setPeopleConsent(v === true)}
                            className="mt-0.5 border-ocean-300/60 data-[state=checked]:bg-ocean-300 data-[state=checked]:text-ocean-900"
                          />
                          <span>
                            <span className="font-semibold">{lcm.personTitle}</span>{" "}
                            {lcm.personText}{" "}
                            <button
                              type="button"
                              onClick={() => setDoc("regler")}
                              className="text-ocean-300 underline underline-offset-2"
                            >
                              {lcm.personRules}
                            </button>
                            .
                          </span>
                        </label>
                      )}

                      <label
                        id="f-villkor"
                        className={`flex scroll-mt-28 items-start gap-3 rounded-md p-3 text-xs leading-relaxed transition-colors ${
                          attempted && !accepted
                            ? "border border-amber-300/60 bg-amber-200/10 text-ocean-50 ring-1 ring-amber-300/40"
                            : "border border-transparent text-ocean-100"
                        }`}
                      >
                        <Checkbox
                          checked={accepted}
                          onCheckedChange={(v) => setAccepted(v === true)}
                          className="mt-0.5 border-ocean-300/60 data-[state=checked]:bg-ocean-300 data-[state=checked]:text-ocean-900"
                        />
                        <span>
                          Jag godkänner{" "}
                          <button
                            type="button"
                            className="text-ocean-300 underline underline-offset-2"
                            onClick={() => setDoc("villkor")}
                          >
                            användarvillkoren
                          </button>{" "}
                          och{" "}
                          <button
                            type="button"
                            onClick={() => setDoc("regler")}
                            className="text-ocean-300 underline underline-offset-2"
                          >
                            annonsreglerna
                          </button>{" "}
                          och intygar att jag har rätt att sälja prylen och till bilderna. Jag är
                          medveten om att Sellavo får dölja, ändra eller radera annonsen och stänga
                          av kontot om innehållet är olämpligt eller bryter mot villkoren. Ditt
                          visningsnamn visas i annonsen. E-post och telefonnummer visas aldrig
                          publikt – de används endast internt av Sellavo, och köpare kontaktar dig
                          via meddelanden {surfaceText.inPlace} enligt{" "}
                          <button
                            type="button"
                            className="text-ocean-300 underline underline-offset-2"
                            onClick={() => setDoc("integritet")}
                          >
                            integritetspolicyn
                          </button>
                          .
                        </span>
                      </label>
                      {planStatus &&
                        (quotaBlocked ? (
                          <div
                            id="f-kvot"
                            className="scroll-mt-28 rounded-md border border-amber-300/60 bg-amber-200/10 p-4 text-sm text-ocean-50"
                          >
                            <p className="font-semibold">{planStatus.quota.reason}</p>
                            <Link
                              to="/priser"
                              search={{ retur: returPath }}
                              onClick={saveBeforeLeaving}
                              className="mt-1 inline-block text-ocean-300 underline underline-offset-2"
                            >
                              Se planer och gränser
                            </Link>
                          </div>
                        ) : (
                          <p className="text-xs text-ocean-100/80">
                            {(() => {
                              const left = remainingThisMonth(activePlan, planStatus.usage);
                              return left === null
                                ? `${PLANS[activePlan].name}: obegränsat antal annonser i månaden.`
                                : `${PLANS[activePlan].name}: ${left} av ${PLANS[activePlan].limits.perMonth} annonser kvar den här månaden.`;
                            })()}{" "}
                            <Link
                              to="/priser"
                              search={{ retur: returPath }}
                              onClick={saveBeforeLeaving}
                              className="text-ocean-300 underline underline-offset-2"
                            >
                              Se planer
                            </Link>
                          </p>
                        ))}

                      {isSuperadmin && (
                        <div className="rounded-lg border border-amber-300/30 bg-amber-200/5 p-4">
                          <label className="flex cursor-pointer items-start gap-3">
                            <Checkbox
                              checked={hiddenAd}
                              onCheckedChange={(v) => setHiddenAd(v === true)}
                              className="mt-0.5"
                            />
                            <span className="min-w-0">
                              <span className="flex items-center gap-2 font-heading text-sm font-semibold text-amber-200">
                                <EyeOff className="h-4 w-4 shrink-0" />
                                Publicera som dold annons
                              </span>
                              <span className="mt-1 block text-xs leading-relaxed text-ocean-100/80">
                                Bara du som superadmin ser annonsen. Den listas inte publikt, syns
                                inte i sökningar och finns inte med i sajtkartan.
                              </span>
                            </span>
                          </label>
                        </div>
                      )}

                      {(() => {
                        const missing: { id: string; label: string }[] = [];
                        if (!form.title.trim())
                          missing.push({ id: "f-title", label: flowText.titleMissing });
                        if (!form.description.trim())
                          missing.push({ id: "f-desc", label: flowText.descriptionMissing });
                        if (!form.price || Number(form.price) <= 0)
                          missing.push({ id: "f-price", label: flowText.priceMissing });
                        if (!form.category)
                          missing.push({ id: "f-cat", label: flowText.categoryMissing });
                        if (!isValidLocalityForCountry(form.location, adCountry))
                          missing.push({
                            id: "f-loc",
                            label: flowText.locationMissing,
                          });
                        if (!form.contactName && !displayNameDraft.trim())
                          missing.push({
                            id: "f-profil",
                            label: "Visningsnamn saknas – fyll i din profil",
                          });

                        if (peopleInImages && !peopleConsent)
                          missing.push({
                            id: "f-personer",
                            label: flowText.consentMissing,
                          });
                        if (!accepted)
                          missing.push({ id: "f-villkor", label: flowText.termsMissing });
                        if (quotaBlocked)
                          missing.push({
                            id: "f-kvot",
                            label: planStatus?.quota.reason ?? flowText.quota,
                          });
                        const blocked = missing.length > 0;
                        const goTo = (id: string) => {
                          const el = document.getElementById(id);
                          if (!el) return;
                          el.scrollIntoView({ behavior: "smooth", block: "center" });
                          const focusable =
                            el instanceof HTMLInputElement ||
                            el instanceof HTMLTextAreaElement ||
                            el instanceof HTMLSelectElement
                              ? el
                              : el.querySelector<HTMLElement>(
                                  "input, textarea, select, [role='combobox']",
                                );
                          if (focusable) focusable.focus({ preventScroll: true });
                        };
                        return (
                          <>
                            {attempted && blocked && (
                              <div className="rounded-md border border-amber-300/60 bg-amber-200/10 p-4 text-sm text-ocean-50">
                                <p className="flex items-center gap-2 font-semibold text-amber-200">
                                  <AlertTriangle className="h-4 w-4 shrink-0" />
                                  Det här saknas
                                </p>
                                <ul className="mt-2 space-y-1 text-xs text-ocean-50">
                                  {missing.map((m) => (
                                    <li key={m.id}>
                                      <button
                                        type="button"
                                        onClick={() => goTo(m.id)}
                                        className="flex w-full items-start gap-2 rounded px-1 py-0.5 text-left transition-colors hover:bg-amber-200/10"
                                      >
                                        <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-amber-300" />
                                        <span className="underline decoration-amber-300/50 underline-offset-2">
                                          {m.label}
                                        </span>
                                      </button>
                                    </li>
                                  ))}
                                </ul>
                              </div>
                            )}
                            <TooltipProvider delayDuration={100}>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <button
                                    type="submit"
                                    disabled={publishing}
                                    onClick={(e) => {
                                      if (!blocked) return;
                                      e.preventDefault();
                                      setAttempted(true);
                                      goTo(missing[0]!.id);
                                    }}
                                    className="inline-flex w-full items-center justify-center gap-2 rounded-lg bg-ocean-300 px-5 py-3 font-heading font-semibold text-ocean-900 transition-colors hover:bg-ocean-300/90 disabled:opacity-60"
                                  >
                                    {publishing ? (
                                      <Loader2 className="h-4 w-4 animate-spin" />
                                    ) : (
                                      <Share2 className="h-4 w-4" />
                                    )}
                                    {publishing ? "Publicerar..." : "Publicera annons"}
                                  </button>
                                </TooltipTrigger>
                                <TooltipContent
                                  side="top"
                                  className="max-w-xs border-amber-300/40 bg-ocean-900 text-ocean-50"
                                >
                                  {blocked ? (
                                    <div className="space-y-1.5">
                                      <p className="flex items-center gap-2 font-semibold text-amber-200">
                                        <AlertTriangle className="h-3.5 w-3.5 shrink-0" />
                                        Det här saknas
                                      </p>
                                      <ul className="space-y-1 text-xs">
                                        {missing.map((m) => (
                                          <li key={m.id} className="flex items-start gap-2">
                                            <span className="mt-1.5 h-1 w-1 shrink-0 rounded-full bg-amber-300" />
                                            {m.label}
                                          </li>
                                        ))}
                                      </ul>
                                    </div>
                                  ) : (
                                    <p className="text-xs">{lcm.publishNow}</p>
                                  )}
                                </TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          </>
                        );
                      })()}
                    </div>
                  )}
                </form>
              )}

              {published && (
                <div className="mt-6 rounded-md border border-ocean-300/30 bg-ocean-700/30 p-6 text-center">
                  <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-ocean-300/20">
                    <Check className="h-6 w-6 text-ocean-300" />
                  </div>
                  <p className="mt-4 font-heading text-lg font-semibold text-white">
                     {flowText.published}
                  </p>
                  {pendingGroups.length ? (
                    <>
                      <p className="mt-1 text-sm text-ocean-100">
                        Du har {pendingGroups.length} prylar kvar att lägga ut
                        {pendingGroups[0]?.label ? ` – näst på tur: ${pendingGroups[0].label}` : ""}
                        . Text och bilder är redan klara.
                      </p>
                      <div className="mt-4 flex flex-wrap items-center justify-center gap-2">
                        <button
                          type="button"
                          onClick={handleNextPending}
                          className="inline-flex items-center gap-2 rounded-lg bg-ocean-300 px-6 py-3 font-heading font-semibold text-ocean-900 transition-colors hover:bg-ocean-300/90"
                        >
                          Skapa nästa annons <ArrowRight className="h-4 w-4" />
                        </button>
                        <Link
                          to="/a/$slug"
                          params={{ slug: published.slug }}
                          search={{ ny: true }}
                          className="inline-flex items-center gap-2 rounded-lg border border-ocean-300/30 bg-ocean-700/30 px-5 py-3 text-sm font-medium text-white transition-colors hover:bg-ocean-700/50"
                        >
                          Visa annonsen
                        </Link>
                      </div>
                    </>
                  ) : (
                    <>
                      <p className="mt-1 text-sm text-ocean-100">
                        Den ligger nu ute och syns för köpare.
                      </p>
                      <Link
                        to="/a/$slug"
                        params={{ slug: published.slug }}
                        search={{ ny: true }}
                        className="mt-4 inline-flex items-center gap-2 rounded-lg bg-ocean-300 px-6 py-3 font-heading font-semibold text-ocean-900 transition-colors hover:bg-ocean-300/90"
                      >
                        Visa annons <ArrowRight className="h-4 w-4" />
                      </Link>
                    </>
                  )}
                </div>
              )}

              {error && (
                <div className="mt-4 rounded-lg border border-destructive/40 bg-destructive/20 px-4 py-3 text-sm text-white">
                  {error}
                </div>
              )}
            </div>
          )}
        </div>
      </section>

      {variant === "home" && (
        <>
          {/* Så fungerar det + videoskanning */}
          <section className="px-4 py-6 sm:px-6 sm:py-12 lg:px-8">
            <div className="mx-auto grid max-w-6xl gap-4 sm:gap-5 lg:grid-cols-2">
              <div className="rounded-xl border border-border bg-card p-4 shadow-sm sm:p-6">
                <h2 className="font-heading text-xl font-bold text-foreground sm:text-3xl">
                  Så fungerar det
                </h2>
                <p className="mt-1 text-sm text-muted-foreground sm:text-base">
                  Fyra steg från foto till publicerad annons – och allt vi skriver kan du ändra.
                </p>
                <ol className="mt-3 grid grid-cols-2 gap-2.5 sm:mt-4 sm:gap-3">
                  {steps.map((step, i) => (
                    <li key={step.title} className="flex items-start gap-2 sm:gap-3">
                      <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-ocean-100 text-ocean-700 sm:h-8 sm:w-8">
                        <step.icon className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                      </span>
                      <span className="min-w-0">
                        <span className="block text-sm font-semibold text-foreground">
                          {i + 1}. {step.title}
                        </span>
                        <span className="mt-0.5 hidden text-sm text-muted-foreground sm:block">
                          {step.description}
                        </span>
                      </span>
                    </li>
                  ))}
                </ol>
                <Link
                  to="/sa-fungerar-det"
                  className="mt-3 inline-flex items-center gap-1.5 text-sm font-semibold text-primary hover:underline sm:mt-4"
                >
                  Läs hur det fungerar <ArrowRight className="h-4 w-4" />
                </Link>
              </div>

              <VideoScanPromo />
              <PhotoScanPromo className="mt-4" />
              {testFeatures && <DeepScanPromo className="mt-4" />}
            </div>
          </section>
          {/* Latest ads */}
          <section className="px-4 pb-10 sm:px-6 sm:pb-20 lg:px-8">
            <div className="mx-auto max-w-6xl">
              <div className="grid grid-cols-[minmax(0,1fr)_auto] items-end gap-3 sm:flex sm:flex-wrap sm:justify-between sm:gap-4">
                <div className="min-w-0">
                  <h2 className="font-heading text-2xl font-bold text-foreground sm:text-3xl">
                    {lcm.latestTitle}
                  </h2>
                  <p className="mt-1 text-sm text-muted-foreground sm:mt-2 sm:text-base">
                    {lcm.latestText}
                  </p>
                </div>
                <Button asChild variant="outline">
                  <Link to={localizedPath("/annonser", lang)}>{lcm.seeAll}</Link>
                </Button>
              </div>

              <Suspense fallback={<LatestAdsSkeleton />}>
                <LatestAdsGrid />
              </Suspense>

              <div className="mt-6 flex flex-wrap justify-center gap-1.5 sm:mt-10 sm:gap-2">
                {CATEGORIES.map((c) => (
                  <Link
                    key={c.slug}
                    to="/annonser"
                    search={{ kategori: c.slug }}
                    className="inline-flex items-center gap-1.5 rounded-full border border-border bg-card px-2.5 py-1 text-xs text-foreground/80 transition-colors hover:border-primary/50 hover:text-foreground sm:rounded-md sm:px-3 sm:py-1.5 sm:text-sm"
                  >
                    <c.icon className="h-3.5 w-3.5 text-muted-foreground sm:h-4 sm:w-4" />
                    {c.label}
                  </Link>
                ))}
              </div>
            </div>
          </section>
        </>
      )}

      {authOpen ? (
        <Suspense fallback={null}>
          <AuthModal
            key={authMode}
            open={authOpen}
            onOpenChange={setAuthOpen}
            defaultMode={authMode}
            onVerified={restoreAfterAuth}
            getDraftPayload={() =>
              images.length
                ? {
                    form: {
                      ...form,
                      saleKind,
                      reservePrice,
                      auctionDays: String(auctionDays),
                      peopleInImages: String(peopleInImages),
                      peopleConsent: String(peopleConsent),
                      accepted: String(accepted),
                      saleAdviceKind: saleAdvice?.kind ?? "",
                      saleAdviceReason: saleAdvice?.reason ?? "",
                      valuationLow: aiValuation ? String(aiValuation.low) : "",
                      valuationHigh: aiValuation?.high ? String(aiValuation.high) : "",
                      priceMotivation,
          hidden: String(hiddenAd) as DraftForm["hidden"],
                    } as Record<string, string>,
                    video: video ?? null,
                    images: images.map((i) => ({
                      imageBase64: i.imageBase64,
                      mimeType: i.mimeType,
                    })),
                  }
                : null
            }
          />
        </Suspense>
      ) : null}

      <AlertDialog open={confirmReset} onOpenChange={setConfirmReset}>
        <AlertDialogContent className="sm:max-w-md">
          <AlertDialogHeader>
            <AlertDialogTitle>{lcm.newAdTitle}</AlertDialogTitle>
            <AlertDialogDescription>
              {lcm.newAdText}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{lcm.keepDraft}</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                handleReset();
                setConfirmReset(false);
              }}
            >
              Rensa och börja om
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {doc ? (
        <Suspense fallback={null}>
          <LegalModal doc={doc} onClose={() => setDoc(null)} />
        </Suspense>
      ) : null}

      {/* Auktion utan reservationspris – varning innan publicering. */}
      <AlertDialog open={reserveConfirm} onOpenChange={setReserveConfirm}>
        <AlertDialogContent className="sm:max-w-md">
          <AlertDialogHeader>
            <div className="mb-1 flex h-12 w-12 items-center justify-center rounded-2xl bg-amber-500/15 ring-1 ring-amber-500/30">
              <TriangleAlert className="h-6 w-6 text-amber-500" aria-hidden />
            </div>
            <AlertDialogTitle>Vill du inte ha ett reservationspris?</AlertDialogTitle>
            <AlertDialogDescription className="text-pretty">
              {reserveWarning?.message} Med ett reservationspris säljs prylen bara om budet når dit
              – annars behåller du den. De flesta sätter det till vad de verkligen tycker att prylen
              är värd.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="sm:flex-col sm:space-x-0 sm:gap-2">
            {reserveWarning?.suggested ? (
              <AlertDialogAction
                onClick={() => {
                  setReservePrice(String(reserveWarning.suggested));
                  setReserveConfirm(false);
                }}
              >
                Sätt {formatKr(reserveWarning.suggested)} som reservationspris
              </AlertDialogAction>
            ) : null}
            <AlertDialogCancel
              className="mt-0"
              onClick={() => {
                setReserveConfirm(false);
                window.setTimeout(() => {
                  const el = document.getElementById("f-reserve");
                  if (el instanceof HTMLInputElement) {
                    scrollToElement(el, 100);
                    el.focus();
                  }
                }, 120);
              }}
            >
              Skriv eget reservationspris
            </AlertDialogCancel>
            <button
              type="button"
              className="mx-auto mt-1 text-sm text-muted-foreground underline-offset-4 hover:text-foreground hover:underline"
              onClick={() => {
                setReserveConfirm(false);
                void runPublish();
              }}
            >
              Fortsätt utan reservationspris
            </button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog
        open={prefillConflict !== null}
        onOpenChange={(open) => !open && keepDraftOverPrefill()}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{lcx.draftTitle}</AlertDialogTitle>
            <AlertDialogDescription>{lcx.draftBodyPrefill}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              onClick={(e) => {
                e.preventDefault();
                void keepDraftOverPrefill();
              }}
            >
              {lcx.draftKeep}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                void startOverWithPrefill();
              }}
            >
              {lcx.draftUseItem}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog
        open={imageToRemove !== null}
        onOpenChange={(open) => !open && setImageToRemove(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{lc.deleteImage}</AlertDialogTitle>
            <AlertDialogDescription>
               {flowText.deleteDescription}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setImageToRemove(null)}>{lc.cancel}</AlertDialogCancel>
            <AlertDialogAction
              onClick={doRemoveImage}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
               {flowText.remove}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={regenerateOpen} onOpenChange={setRegenerateOpen}>
        <AlertDialogContent className="border-ocean-300/30 bg-ocean-900 text-white">
          <AlertDialogHeader>
             <AlertDialogTitle className="text-white">{lcm.rewriteTitle}</AlertDialogTitle>
            <AlertDialogDescription className="text-ocean-100">
               {lcm.rewriteText}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              onClick={() => setRegenerateOpen(false)}
              className="border-ocean-300/30 bg-ocean-800 text-white hover:bg-ocean-700 hover:text-white"
            >
               {lc.cancel}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                setRegenerateOpen(false);
                void handleGenerate();
              }}
              className="bg-ocean-300 text-ocean-900 hover:bg-ocean-300/90"
            >
              Ja, skriv om
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {aiProgress ? (
        <Suspense fallback={null}>
          <AiProgressModal state={aiProgress} />
        </Suspense>
      ) : null}
      <AlertDialog open={!!priceApproval} onOpenChange={(o) => !o && setPriceApproval(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{lcm.priceTitle}</AlertDialogTitle>
            <AlertDialogDescription>
              {priceApproval?.min && priceApproval?.max
                ? lcm.priceRange(
                    `${priceApproval.min.toLocaleString(moneyLocale)} ${moneyUnit}`,
                    `${priceApproval.max.toLocaleString(moneyLocale)} ${moneyUnit}`,
                  )
                : lcm.priceHigh}{" "}
              {lcm.priceTail}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <Textarea
            rows={3}
            placeholder={lcm.priceMotivationPlaceholder}
            value={priceMotivation}
            onChange={(e) => setPriceMotivation(e.target.value)}
          />
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setPriceApproval(null)}>
              Ändra priset
            </AlertDialogCancel>
            <AlertDialogAction
              disabled={priceMotivation.trim().length < 10}
              onClick={() => {
                const motivation = priceMotivation.trim();
                setPriceApproval(null);
                setPriceMotivation("");
                void runPublish(undefined, {
                  skipPriceWarning: true,
                  priceApprovalMotivation: motivation,
                });
              }}
            >
              Skicka för godkännande
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      {publishFlow ? (
        <Suspense fallback={null}>
          <PublishFlowModal
            state={publishFlow}
            promoting={promoting}
            onClose={() => setPublishFlow(null)}
            onFix={goToIssue}
            onContinueAnyway={() =>
              void runPublish(undefined, {
                skipPriceWarning: true,
                skipQuality: true,
                skipImageWarning: true,
              })
            }
            rewriting={rewriting}
            onSplitAds={() => {
              setPublishFlow(null);
              if ((splitInfo?.groups.length ?? 0) > 1) {
                handleSplit();
                return;
              }
              // Utan bildgrupper delar vi upp i två steg: behåll huvudvaran nu och
              // lägg ut resten som egna annonser efteråt.
              if (qualityRewrite) {
                const next = qualityRewrite;
                setForm((f) => ({ ...f, title: next.title, description: next.description }));
                setQualityRewrite(null);
              }
              toast.info("Vi har rensat annonsen till en pryl", {
                description:
                  "Publicera den här först – lägg sedan ut de andra sakerna som egna annonser.",
              });
              scrollToReview();
            }}
            onUseRewrite={() => {
              if (!qualityRewrite) return;
              const next = qualityRewrite;
              setRewriting(true);
              setForm((f) => ({ ...f, title: next.title, description: next.description }));
              setQualityRewrite(null);
              void runPublish(next, { skipQuality: true }).finally(() => setRewriting(false));
            }}
            onPublishAnother={() => {
              setPublishFlow(null);
              if (pendingGroups.length) {
                handleNextPending();
                return;
              }
              handleReset();
              window.setTimeout(() => {
                document.getElementById("skapa")?.scrollIntoView({ behavior: "smooth" });
              }, 100);
            }}
            onPromote={(days) => void handlePromoteNow(days)}
          />
        </Suspense>
      ) : null}
      <AlertDialog open={confirmReset} onOpenChange={setConfirmReset}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{lcm.restartTitle}</AlertDialogTitle>
            <AlertDialogDescription>{lcm.restartText}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{lc.cancel}</AlertDialogCancel>
            <AlertDialogAction onClick={() => handleReset()}>{lc.startOver}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {highValue && testFeatures && images.length ? (
        <Suspense fallback={null}>
          <HighValueDialog
            reason={highValue.reason}
            range={{ low: highValue.low, high: highValue.high }}
            name={form.title || "Pryl från annons"}
            note={form.description}
            category={form.category}
            price={Number(form.price) || undefined}
            images={images.slice(0, 4).map((i) => ({
              imageBase64: i.imageBase64,
              mimeType: i.mimeType,
            }))}
            onClose={() => setHighValue(null)}
            onPrice={(price) => {
              setForm((f) => ({ ...f, price: String(price) }));
              toast.success("Prisförslaget kommer från djupvärderingen.");
            }}
            onAdText={({ title, text }) => {
              setForm((f) => ({
                ...f,
                title: title ? title.slice(0, 70) : f.title,
                description: text || f.description,
              }));
              toast.success("Rubrik och text kommer från djupvärderingen.", {
                description: "Läs igenom och ändra det som inte stämmer.",
              });
            }}
          />
        </Suspense>
      ) : null}

      {adminTrace && adminTrace.length > 0 ? (
        <Suspense fallback={null}>
          <AdminAiLog steps={adminTrace} className="mt-8" />
        </Suspense>
      ) : null}
    </main>
  );
}

function HeroStart({
  asLabel,
  pressed,
  setPressed,
  onOpenPicker,
  onFileChange,
}: {
  asLabel: boolean;
  pressed: boolean;
  setPressed: (v: boolean) => void;
  onOpenPicker: () => void;
  onFileChange: (input: HTMLInputElement) => void | Promise<void>;
}) {
  const className = cn(
    "group relative mx-auto mt-6 block w-full max-w-xl cursor-pointer rounded-2xl border border-white/10 bg-white/5 p-6 text-center backdrop-blur-sm transition-all duration-300",
    "hover:-translate-y-0.5 hover:scale-[1.01] hover:border-ocean-300/30 hover:bg-white/10 hover:shadow-xl hover:shadow-ocean-900/20",
    "sm:mt-10 sm:p-10",
    pressed
      ? "is-pressed -translate-y-0.5 scale-[1.01] border-ocean-300/30 bg-white/10 shadow-xl shadow-ocean-900/20"
      : "active:translate-y-0 active:scale-[0.99]",
  );

  const inner = (
    <>
      <div className="mx-auto grid h-16 w-16 place-items-center rounded-2xl bg-gradient-to-br from-ocean-300 to-ocean-500 shadow-lg shadow-ocean-900/30 transition-transform duration-300 group-hover:scale-110 group-[.is-pressed]:scale-110 sm:h-20 sm:w-20">
        <Camera className="h-7 w-7 text-ocean-900 sm:h-9 sm:w-9" />
      </div>
      <p className="mt-4 font-heading text-lg font-bold text-white sm:text-2xl">
        Fota din pryl – vi skriver annonsen
      </p>
      <p className="mx-auto mt-2 max-w-sm text-sm leading-relaxed text-ocean-100">
        Ta en bild, få färdig text och prisförslag och publicera gratis. Utkastet sparas automatiskt
        medan du jobbar.
      </p>
      <div className="relative mt-6 inline-flex items-center gap-2 overflow-hidden rounded-xl bg-ocean-300 px-7 py-3.5 font-heading font-semibold text-ocean-900 shadow-md transition-all duration-300 group-hover:bg-ocean-200 group-hover:shadow-xl group-hover:shadow-ocean-300/30 group-[.is-pressed]:bg-ocean-200 group-[.is-pressed]:shadow-xl group-[.is-pressed]:shadow-ocean-300/30">
        <span className="pointer-events-none absolute inset-y-0 -left-1/3 w-1/3 -skew-x-12 bg-white/50 opacity-0 group-hover:animate-[shine_0.9s_ease-out] group-hover:opacity-100 group-[.is-pressed]:animate-[shine_0.9s_ease-out] group-[.is-pressed]:opacity-100" />
        Kom igång
        <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1 group-[.is-pressed]:translate-x-1" />
      </div>
      <p className="mt-4 text-xs font-medium text-ocean-200/70">Det tar mindre än 30 sekunder</p>
    </>
  );

  if (asLabel) {
    return (
      <label
        onTouchStart={() => setPressed(true)}
        onTouchEnd={() => setPressed(false)}
        onTouchCancel={() => setPressed(false)}
        onTouchMove={() => setPressed(false)}
        className={className}
      >
        {inner}
        <input
          type="file"
          accept={IMAGE_ACCEPT}
          multiple
          className="hidden"
          onChange={(e) => void onFileChange(e.currentTarget)}
        />
      </label>
    );
  }

  return (
    <button type="button" onClick={onOpenPicker} className={className}>
      {inner}
    </button>
  );
}
